import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Student, Teacher, SchoolSettings, AttendanceRecord, AttendanceStatus, HomeroomJournalNote, AgendaEntry, ScheduleEntry, isSameClass, normalizeClassName } from '../../types.ts';
import { getThemeById } from '../themeConfig.ts';
import { 
  CalendarDays, 
  Search, 
  Printer, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  DoorOpen, 
  Heart, 
  FileText, 
  AlertTriangle, 
  BarChart2, 
  CheckSquare, 
  Calendar,
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Filter,
  Check,
  Award,
  Sparkles,
  Settings,
  CalendarRange,
  RotateCcw,
  Sliders,
  Info,
  X,
  Plus,
  ChevronDown,
  Cloud,
  CheckCircle,
  Loader2,
  BookOpen,
  RefreshCw,
  Zap,
  CheckCheck,
  AlertOctagon
} from 'lucide-react';

interface ClassAttendanceManagerProps {
  homeroomClass: string;
  students: Student[];
  activeUser: Teacher;
  schoolSettings: SchoolSettings;
  attendanceRecords?: AttendanceRecord[];
  onUpdateAttendance?: (records: AttendanceRecord[]) => void | Promise<any>;
  onUpdateSettings?: (settings: SchoolSettings) => void | Promise<any>;
  homeroomNotes?: HomeroomJournalNote[];
  onSaveHomeroomNote?: (note: HomeroomJournalNote, method: 'POST' | 'PUT' | 'DELETE') => void | Promise<any>;
  appTheme?: string;
  agendas?: AgendaEntry[];
  agendaAttendanceRecords?: AttendanceRecord[];
  schedules?: ScheduleEntry[];
}

const INDONESIAN_MONTHS = [
  { value: '01', name: 'Januari' },
  { value: '02', name: 'Februari' },
  { value: '03', name: 'Maret' },
  { value: '04', name: 'April' },
  { value: '05', name: 'Mei' },
  { value: '06', name: 'Juni' },
  { value: '07', name: 'Juli' },
  { value: '08', name: 'Agustus' },
  { value: '09', name: 'September' },
  { value: '10', name: 'Oktober' },
  { value: '11', name: 'November' },
  { value: '12', name: 'Desember' },
];

const ATTENDANCE_STATUS_CONFIG: Record<string, {
  label: string;
  short: string;
  bg: string;
  text: string;
  badge: string;
  btnActive: string;
  btnInactive: string;
}> = {
  [AttendanceStatus.Hadir]: {
    label: 'Hadir',
    short: 'H',
    bg: 'bg-emerald-600',
    text: 'text-emerald-700 dark:text-emerald-300',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800',
    btnActive: 'bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-400 font-black',
    btnInactive: 'bg-slate-100 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 hover:text-emerald-600',
  },
  [AttendanceStatus.Sakit]: {
    label: 'Sakit',
    short: 'S',
    bg: 'bg-blue-600',
    text: 'text-blue-700 dark:text-blue-300',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-950/80 dark:text-blue-300 border-blue-300 dark:border-blue-800',
    btnActive: 'bg-blue-600 text-white shadow-sm ring-2 ring-blue-400 font-black',
    btnInactive: 'bg-slate-100 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:text-blue-600',
  },
  [AttendanceStatus.Izin]: {
    label: 'Izin',
    short: 'I',
    bg: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-300',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border-amber-300 dark:border-amber-800',
    btnActive: 'bg-amber-500 text-white shadow-sm ring-2 ring-amber-400 font-black',
    btnInactive: 'bg-slate-100 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-amber-50 dark:hover:bg-amber-950/40 hover:text-amber-600',
  },
  [AttendanceStatus.Alpa]: {
    label: 'Alpa',
    short: 'A',
    bg: 'bg-rose-600',
    text: 'text-rose-700 dark:text-rose-300',
    badge: 'bg-rose-100 text-rose-800 dark:bg-rose-950/80 dark:text-rose-300 border-rose-300 dark:border-rose-800',
    btnActive: 'bg-rose-600 text-white shadow-sm ring-2 ring-rose-400 font-black',
    btnInactive: 'bg-slate-100 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-600',
  },
  [AttendanceStatus.Terlambat]: {
    label: 'Terlambat',
    short: 'T',
    bg: 'bg-purple-600',
    text: 'text-purple-700 dark:text-purple-300',
    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-950/80 dark:text-purple-300 border-purple-300 dark:border-purple-800',
    btnActive: 'bg-purple-600 text-white shadow-sm ring-2 ring-purple-400 font-black',
    btnInactive: 'bg-slate-100 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-purple-50 dark:hover:bg-purple-950/40 hover:text-purple-600',
  },
  [AttendanceStatus.Pulang]: {
    label: 'Pulang Sebelum Waktunya',
    short: 'P',
    bg: 'bg-orange-500',
    text: 'text-orange-700 dark:text-orange-300',
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-950/80 dark:text-orange-300 border-orange-300 dark:border-orange-800',
    btnActive: 'bg-orange-500 text-white shadow-sm ring-2 ring-orange-400 font-black',
    btnInactive: 'bg-slate-100 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-orange-50 dark:hover:bg-orange-950/40 hover:text-orange-600',
  },
};

const getManagerStatusCfg = (st: any) => {
  if (!st) return ATTENDANCE_STATUS_CONFIG[AttendanceStatus.Hadir];
  return ATTENDANCE_STATUS_CONFIG[st as AttendanceStatus] || ATTENDANCE_STATUS_CONFIG[st.toUpperCase() as AttendanceStatus] || {
    label: st.toString(),
    short: (st.toString()[0] || 'H').toUpperCase(),
    bg: 'bg-slate-600',
    text: 'text-slate-700 dark:text-slate-300',
    badge: 'bg-slate-100 text-slate-800',
    btnActive: 'bg-slate-600 text-white font-black',
    btnInactive: 'bg-slate-100 text-slate-600'
  };
};

const safeFormatDate = (dateStr: string | null | undefined, options?: Intl.DateTimeFormatOptions, fallback: string = '-') => {
  if (!dateStr) return fallback;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('id-ID', options);
};

const getWeekDates = (baseDateStr: string) => {
  let base = new Date(baseDateStr);
  if (!baseDateStr || isNaN(base.getTime())) {
    base = new Date();
  }
  const dayOfWeek = base.getDay(); // 0 is Sun, 1 is Mon...
  const distanceToMon = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(base);
  monday.setDate(base.getDate() + distanceToMon);

  const week = [];
  const dayNames = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
  for (let i = 0; i < 6; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const dateStr = d.toISOString().split('T')[0];
    week.push({
      dateStr,
      dayName: dayNames[i],
      formattedDate: `${d.getDate()}/${d.getMonth() + 1}`,
    });
  }
  return week;
};

interface TopScrollTableProps {
  children: React.ReactNode;
  className?: string;
  tableContainerClassName?: string;
}

/**
 * TopScrollTable
 * Component wrapper yang memindahkan scrollbar horizontal ke ATAS tabel (dengan ukuran scrollbar yang diperbesar)
 * dan tersinkronisasi 2 arah dengan tabel di bawahnya.
 */
const TopScrollTable: React.FC<TopScrollTableProps> = ({
  children,
  className = '',
  tableContainerClassName = ''
}) => {
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const isSyncingRef = useRef<string | null>(null);

  const [scrollWidth, setScrollWidth] = useState<number>(0);
  const [hasOverflow, setHasOverflow] = useState<boolean>(false);
  const [scrollPos, setScrollPos] = useState<{ left: number; max: number }>({ left: 0, max: 0 });

  const updateDimensions = useCallback(() => {
    if (!bottomScrollRef.current) return;
    const el = bottomScrollRef.current;
    const sWidth = el.scrollWidth;
    const cWidth = el.clientWidth;
    const maxScroll = Math.max(0, sWidth - cWidth);
    setScrollWidth(sWidth);
    setHasOverflow(sWidth > cWidth + 5);
    setScrollPos({ left: el.scrollLeft, max: maxScroll });
  }, []);

  useEffect(() => {
    updateDimensions();

    const bottomEl = bottomScrollRef.current;
    if (!bottomEl) return;

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });
      resizeObserver.observe(bottomEl);
      if (bottomEl.firstElementChild) {
        resizeObserver.observe(bottomEl.firstElementChild);
      }
    }

    const timer = setTimeout(updateDimensions, 100);
    window.addEventListener('resize', updateDimensions);

    return () => {
      clearTimeout(timer);
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, [updateDimensions]);

  const handleTopScroll = () => {
    if (isSyncingRef.current === 'bottom') return;
    if (topScrollRef.current && bottomScrollRef.current) {
      isSyncingRef.current = 'top';
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
      const el = bottomScrollRef.current;
      setScrollPos({ left: el.scrollLeft, max: Math.max(0, el.scrollWidth - el.clientWidth) });
      requestAnimationFrame(() => {
        isSyncingRef.current = null;
      });
    }
  };

  const handleBottomScroll = () => {
    if (isSyncingRef.current === 'top') return;
    if (topScrollRef.current && bottomScrollRef.current) {
      isSyncingRef.current = 'bottom';
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
      const el = bottomScrollRef.current;
      setScrollPos({ left: el.scrollLeft, max: Math.max(0, el.scrollWidth - el.clientWidth) });
      requestAnimationFrame(() => {
        isSyncingRef.current = null;
      });
    }
  };

  const scrollByAmount = (amount: number) => {
    if (bottomScrollRef.current) {
      bottomScrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  return (
    <div className={`w-full flex flex-col gap-2 ${className}`}>
      {/* Scrollbar Atas (Enlarged / Thick Draggable Bar with Navigation Buttons) */}
      {hasOverflow && (
        <div className="bg-slate-100/90 dark:bg-slate-900/90 p-2 sm:p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-2xs flex flex-col gap-1.5 transition-all">
          <div className="flex items-center justify-between text-xs px-1 gap-2">
            <div className="flex items-center gap-1.5 font-bold text-slate-700 dark:text-slate-200">
              <span className="p-1 bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 rounded-md shrink-0">
                <ChevronLeft size={13} className="inline -mr-1" />
                <ChevronRight size={13} className="inline -ml-1" />
              </span>
              <span className="font-extrabold text-[11px] sm:text-xs">Scroll Tabel (Atas)</span>
              <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 hidden md:inline">
                • Geser bar di bawah atau klik tombol untuk melihat seluruh kolom
              </span>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => scrollByAmount(-250)}
                disabled={scrollPos.left <= 0}
                className="px-2.5 py-1 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:pointer-events-none border border-slate-200 dark:border-slate-700 rounded-lg text-[11px] font-bold text-slate-700 dark:text-slate-200 shadow-2xs flex items-center gap-1 transition-all active:scale-95 cursor-pointer"
                title="Geser Kiri"
              >
                <ChevronLeft size={13} />
                <span className="hidden xs:inline">Kiri</span>
              </button>
              <button
                type="button"
                onClick={() => scrollByAmount(250)}
                disabled={scrollPos.left >= scrollPos.max - 2}
                className="px-2.5 py-1 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:pointer-events-none border border-slate-200 dark:border-slate-700 rounded-lg text-[11px] font-bold text-slate-700 dark:text-slate-200 shadow-2xs flex items-center gap-1 transition-all active:scale-95 cursor-pointer"
                title="Geser Kanan"
              >
                <span className="hidden xs:inline">Kanan</span>
                <ChevronRight size={13} />
              </button>
            </div>
          </div>

          {/* Draggable Large Top Scrollbar Track */}
          <div
            ref={topScrollRef}
            onScroll={handleTopScroll}
            className="w-full overflow-x-auto overflow-y-hidden top-scrollbar-track rounded-full border border-slate-200 dark:border-slate-700/80 bg-slate-200/70 dark:bg-slate-800/90"
          >
            <div style={{ width: `${scrollWidth}px`, height: '1px' }} />
          </div>
        </div>
      )}

      {/* Main Table Container with enlarged scrollbar */}
      <div
        ref={bottomScrollRef}
        onScroll={handleBottomScroll}
        className={`overflow-x-auto scrollbar-thick rounded-xl border border-slate-200 dark:border-slate-700 ${tableContainerClassName}`}
      >
        {children}
      </div>
    </div>
  );
};

const ClassAttendanceManager: React.FC<ClassAttendanceManagerProps> = ({
  homeroomClass,
  students = [],
  activeUser,
  schoolSettings,
  attendanceRecords = [],
  onUpdateAttendance,
  onUpdateSettings,
  homeroomNotes = [],
  onSaveHomeroomNote,
  appTheme = 'wzc-themes',
  agendas = [],
  agendaAttendanceRecords = [],
  schedules = [],
}) => {
  const currentTheme = useMemo(() => getThemeById(appTheme), [appTheme]);

  // Mode Tampilan: 'harian' | 'mingguan' | 'bulanan' | 'semester' | 'tahunan'
  const [viewMode, setViewMode] = useState<'harian' | 'mingguan' | 'bulanan' | 'semester' | 'tahunan'>('semester');

  // Today's date string
  const todayStr = new Date().toISOString().split('T')[0];

  // Selected Date / Month / Year
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const m = new Date().getMonth() + 1;
    return m < 10 ? `0${m}` : `${m}`;
  });
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  // Filter agendas for this homeroom class
  const classAgendas = useMemo(() => {
    return (agendas || []).filter((a) => a.class && isSameClass(a.class, homeroomClass));
  }, [agendas, homeroomClass]);

  // Map of date string -> agendas for this class
  const classAgendasByDate = useMemo(() => {
    const map: Record<string, AgendaEntry[]> = {};
    classAgendas.forEach((a) => {
      if (!a.date) return;
      if (!map[a.date]) map[a.date] = [];
      map[a.date].push(a);
    });
    return map;
  }, [classAgendas]);

  // Combined effective attendance records:
  // Starts with Walas attendance records, and integrates / overlays attendance from Class Agendas
  const effectiveAttendanceRecords = useMemo(() => {
    const map = new Map<string, AttendanceRecord & { isFromAgenda?: boolean; agendaSubject?: string; agendaTeacher?: string }>();

    // 1. First add homeroom records
    (attendanceRecords || []).forEach((r) => {
      if (r.class && !isSameClass(r.class, homeroomClass)) return;
      map.set(`${r.studentId}_${r.date}`, { ...r });
    });

    // 2. Process all class agendas and their attendance records
    classAgendas.forEach((agenda) => {
      const dateStr = agenda.date;
      if (!dateStr) return;

      const recs = (agendaAttendanceRecords || []).filter(
        (r) => r.agendaId === agenda.id || (r.date === dateStr && r.class && isSameClass(r.class, homeroomClass) && r.agendaId !== 'wk_attendance')
      );

      recs.forEach((ar) => {
        const key = `${ar.studentId}_${dateStr}`;
        const existing = map.get(key);

        // Priority for status: Non-Hadir (Sakit, Izin, Alpa, Terlambat, Pulang) takes priority over default Hadir
        const isArNonHadir = ar.status !== AttendanceStatus.Hadir;
        const isExistingDefaultHadir = existing && existing.status === AttendanceStatus.Hadir && !existing.description;

        if (!existing || isExistingDefaultHadir || (isArNonHadir && existing.status === AttendanceStatus.Hadir)) {
          let mergedNote = ar.description || '';
          if (existing?.description && existing.description !== ar.description && !existing.description.includes(ar.description || '')) {
            mergedNote = existing.description + (mergedNote ? ` | ${mergedNote}` : '');
          }

          map.set(key, {
            id: existing?.id || ar.id || `eff_${ar.studentId}_${dateStr}`,
            studentId: ar.studentId,
            date: dateStr,
            status: ar.status,
            description: mergedNote,
            class: homeroomClass,
            teacherId: ar.teacherId || agenda.teacherId || activeUser?.id,
            agendaId: agenda.id,
            isFromAgenda: true,
            agendaSubject: agenda.subject,
            agendaTeacher: agenda.teacherName,
          });
        }
      });
    });

    return Array.from(map.values());
  }, [attendanceRecords, classAgendas, agendaAttendanceRecords, homeroomClass, activeUser]);

  // Feedback state when synchronizing
  const [syncFeedback, setSyncFeedback] = useState<string | null>(null);

  const handleSyncFromAgenda = () => {
    if (!onUpdateAttendance) return;
    onUpdateAttendance(effectiveAttendanceRecords);
    setSyncFeedback('Data absensi dari agenda pembelajaran berhasil disinkronkan ke data wali kelas!');
    setTimeout(() => setSyncFeedback(null), 4000);
  };

  // Agendas & Schedules on selectedDate
  const agendasOnSelectedDate = useMemo(() => {
    return classAgendas.filter((a) => a.date === selectedDate);
  }, [classAgendas, selectedDate]);

  const schedulesOnSelectedDate = useMemo(() => {
    if (!schedules || schedules.length === 0) return [];
    const d = new Date(selectedDate);
    const daysIndo = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    const dayName = daysIndo[d.getDay()];
    return schedules.filter((s) => isSameClass(s.class, homeroomClass) && s.day === dayName);
  }, [schedules, selectedDate, homeroomClass]);

  const handlePrevMonth = () => {
    const m = parseInt(selectedMonth, 10);
    if (m === 1) {
      setSelectedMonth('12');
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(String(m - 1).padStart(2, '0'));
    }
  };

  const handleNextMonth = () => {
    const m = parseInt(selectedMonth, 10);
    if (m === 12) {
      setSelectedMonth('01');
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(String(m + 1).padStart(2, '0'));
    }
  };

  // Helper to extract default semester dates
  const getDefaultStartDate = () => {
    const curMonth = new Date().getMonth() + 1;
    const curYear = new Date().getFullYear();
    return curMonth >= 7 ? `${curYear}-07-14` : `${curYear}-01-05`;
  };

  const getDefaultEndDate = () => {
    const curMonth = new Date().getMonth() + 1;
    const curYear = new Date().getFullYear();
    return curMonth >= 7 ? `${curYear}-12-19` : `${curYear}-06-19`;
  };

  // Semester Start & End Date State (Awal Masuk Sekolah & Akhir Semester)
  const [semesterStartDate, setSemesterStartDate] = useState<string>(() => {
    if (schoolSettings?.classSemesterDates?.[homeroomClass]?.startDate) {
      return schoolSettings.classSemesterDates[homeroomClass].startDate;
    }
    if (schoolSettings?.semesterStartDate) return schoolSettings.semesterStartDate;
    try {
      const saved = localStorage.getItem(`smpn1_semester_start_${homeroomClass}`) || localStorage.getItem('smpn1_semester_start_date');
      if (saved) return saved;
    } catch {}
    return getDefaultStartDate();
  });

  const [semesterEndDate, setSemesterEndDate] = useState<string>(() => {
    if (schoolSettings?.classSemesterDates?.[homeroomClass]?.endDate) {
      return schoolSettings.classSemesterDates[homeroomClass].endDate;
    }
    if (schoolSettings?.semesterEndDate) return schoolSettings.semesterEndDate;
    try {
      const saved = localStorage.getItem(`smpn1_semester_end_${homeroomClass}`) || localStorage.getItem('smpn1_semester_end_date');
      if (saved) return saved;
    } catch {}
    return getDefaultEndDate();
  });

  // Custom Holidays State
  const [customHolidays, setCustomHolidays] = useState<{ date: string; description: string }[]>(() => {
    if (schoolSettings?.classCustomHolidays?.[homeroomClass] && Array.isArray(schoolSettings.classCustomHolidays[homeroomClass])) {
      return schoolSettings.classCustomHolidays[homeroomClass];
    }
    if (schoolSettings?.customHolidays && Array.isArray(schoolSettings.customHolidays) && schoolSettings.customHolidays.length > 0) {
      return schoolSettings.customHolidays;
    }
    try {
      const saved = localStorage.getItem(`smpn1_custom_holidays_${homeroomClass}`) || localStorage.getItem('smpn1_custom_holidays');
      if (saved) return JSON.parse(saved);
    } catch {}
    return [];
  });

  // Auto-sync state when schoolSettings prop arrives from Firebase
  useEffect(() => {
    if (!schoolSettings) return;

    // 1. Sync custom holidays from cloud
    const cloudHolidays = schoolSettings.classCustomHolidays?.[homeroomClass] || schoolSettings.customHolidays;
    if (cloudHolidays && Array.isArray(cloudHolidays)) {
      setCustomHolidays(cloudHolidays);
      try {
        localStorage.setItem(`smpn1_custom_holidays_${homeroomClass}`, JSON.stringify(cloudHolidays));
      } catch (e) {}
    }

    // 2. Sync semester start date from cloud
    const cloudStartDate = schoolSettings.classSemesterDates?.[homeroomClass]?.startDate || schoolSettings.semesterStartDate;
    if (cloudStartDate) {
      setSemesterStartDate(cloudStartDate);
      try {
        localStorage.setItem(`smpn1_semester_start_${homeroomClass}`, cloudStartDate);
      } catch (e) {}
    }

    // 3. Sync semester end date from cloud
    const cloudEndDate = schoolSettings.classSemesterDates?.[homeroomClass]?.endDate || schoolSettings.semesterEndDate;
    if (cloudEndDate) {
      setSemesterEndDate(cloudEndDate);
      try {
        localStorage.setItem(`smpn1_semester_end_${homeroomClass}`, cloudEndDate);
      } catch (e) {}
    }
  }, [schoolSettings, homeroomClass]);

  const [tempHolidays, setTempHolidays] = useState<{ date: string; description: string }[]>(customHolidays);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayDesc, setNewHolidayDesc] = useState('');

  const holidaySet = useMemo(() => new Set(customHolidays.map(h => h.date)), [customHolidays]);
  const tempHolidaySet = useMemo(() => new Set(tempHolidays.map(h => h.date)), [tempHolidays]);

  // Auto-create journal entries for any late attendance records with Status 'Dalam Proses'
  useEffect(() => {
    if (!onSaveHomeroomNote || !effectiveAttendanceRecords || effectiveAttendanceRecords.length === 0) return;

    const lateRecords = effectiveAttendanceRecords.filter((r) => r.status === AttendanceStatus.Terlambat);
    if (lateRecords.length === 0) return;

    lateRecords.forEach((rec) => {
      const exists = homeroomNotes?.some(
        (n) => n.studentId === rec.studentId && n.date === rec.date && (n.category === 'Kedisiplinan' || n.category === 'Kehadiran') && (n.description.toLowerCase().includes('terlambat') || n.description.toLowerCase().includes('keterlambatan'))
      );

      if (!exists) {
        const studentObj = students.find((s) => s.id === rec.studentId);
        const studentName = studentObj ? studentObj.name : 'Siswa';

        const lateNote: HomeroomJournalNote = {
          id: `wk_note_late_${rec.studentId}_${rec.date.replace(/-/g, '')}_${Date.now()}`,
          date: rec.date,
          homeroomClass: homeroomClass,
          studentId: rec.studentId,
          studentName: studentName,
          category: 'Kedisiplinan',
          description: `Siswa terlambat hadir pada tanggal ${rec.date} (Absensi Harian Kelas ${homeroomClass}).${rec.description ? ` Keterangan: ${rec.description}` : ''}`,
          actionTaken: 'Telah dicatat otomatis dari absensi harian kelas. Memerlukan pembinaan wali kelas.',
          status: 'Dalam Proses',
          teacherId: activeUser?.id,
          teacherName: activeUser?.name,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        onSaveHomeroomNote(lateNote, 'POST');
      }
    });
  }, [effectiveAttendanceRecords, homeroomClass, homeroomNotes, onSaveHomeroomNote, students, activeUser]);

  // Modal State for Setting Semester Dates
  const [isSettingSemesterOpen, setIsSettingSemesterOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(semesterStartDate);
  const [tempEndDate, setTempEndDate] = useState(semesterEndDate);
  const [tempSemesterName, setTempSemesterName] = useState(schoolSettings?.semester || (new Date().getMonth() + 1 >= 7 ? 'Ganjil' : 'Genap'));
  const [showSemesterDateDetails, setShowSemesterDateDetails] = useState(false);
  const [isPrintDropdownOpen, setIsPrintDropdownOpen] = useState(false);
  const [isSavingSemesterSettings, setIsSavingSemesterSettings] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string | null>(null);

  // Modal State for Attendance Analysis (Analisis Ketidakhadiran Siswa - Dipisah 2 Popup)
  const [isPeriodSelectModalOpen, setIsPeriodSelectModalOpen] = useState(false);
  const [isAnalysisResultModalOpen, setIsAnalysisResultModalOpen] = useState(false);
  const [analysisScope, setAnalysisScope] = useState<'bulanan' | 'seluruh'>('bulanan');
  const [analysisSelectedMonth, setAnalysisSelectedMonth] = useState<string>(selectedMonth);
  const [analysisSelectedYear, setAnalysisSelectedYear] = useState<number>(selectedYear);
  const [analysisSearchQuery, setAnalysisSearchQuery] = useState('');
  const [analysisFilterOnlySIA, setAnalysisFilterOnlySIA] = useState(false);
  const [analysisSortBy, setAnalysisSortBy] = useState<'name' | 'sia' | 'nis' | 'pct'>('name');

  // List of all active effective school dates in semester range
  const semesterDateList = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const todayStrLocal = today.toISOString().split('T')[0];
    const effectiveEnd = semesterEndDate < todayStrLocal ? semesterEndDate : todayStrLocal;

    const dates: { dateStr: string; dayName: string; formatted: string; monthKey: string }[] = [];
    let curr = new Date(semesterStartDate);
    const endD = new Date(effectiveEnd);
    const dayNames = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    
    while (curr <= endD) {
      const day = curr.getDay();
      if (day !== 0 && day !== 6) {
        const dateStr = curr.toISOString().split('T')[0];
        if (!holidaySet.has(dateStr)) {
          const monthKey = dateStr.substring(0, 7); // YYYY-MM
          dates.push({
            dateStr,
            dayName: dayNames[day],
            formatted: safeFormatDate(dateStr, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
            monthKey
          });
        }
      }
      curr.setDate(curr.getDate() + 1);
    }
    return dates;
  }, [semesterStartDate, semesterEndDate, holidaySet]);

  // Color palette for different months
  const monthColorPalette = [
    'bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-800 text-sky-900 dark:text-sky-200',
    'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200',
    'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200',
    'bg-purple-50 dark:bg-purple-950/40 border-purple-200 dark:border-purple-800 text-purple-900 dark:text-purple-200',
    'bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800 text-rose-900 dark:text-rose-200',
    'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-200',
    'bg-teal-50 dark:bg-teal-950/40 border-teal-200 dark:border-teal-800 text-teal-900 dark:text-teal-200',
    'bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-800 text-orange-900 dark:text-orange-200',
    'bg-cyan-50 dark:bg-cyan-950/40 border-cyan-200 dark:border-cyan-800 text-cyan-900 dark:text-cyan-200',
    'bg-fuchsia-50 dark:bg-fuchsia-950/40 border-fuchsia-200 dark:border-fuchsia-800 text-fuchsia-900 dark:text-fuchsia-200'
  ];

  const monthColorMap = useMemo(() => {
    const map = new Map<string, string>();
    const uniqueMonths = Array.from(new Set(semesterDateList.map(d => d.monthKey)));
    uniqueMonths.forEach((m, idx) => {
      map.set(m, monthColorPalette[idx % monthColorPalette.length]);
    });
    return map;
  }, [semesterDateList]);

  const getMonthSolidBg = (monthKey: string) => {
    const uniqueMonths = Array.from(new Set(semesterDateList.map(d => d.monthKey)));
    const idx = uniqueMonths.indexOf(monthKey);
    const colors = [
      'bg-sky-100/90 dark:bg-sky-950/60',
      'bg-emerald-100/90 dark:bg-emerald-950/60',
      'bg-amber-100/90 dark:bg-amber-950/60',
      'bg-purple-100/90 dark:bg-purple-950/60',
      'bg-rose-100/90 dark:bg-rose-950/60',
      'bg-indigo-100/90 dark:bg-indigo-950/60',
      'bg-teal-100/90 dark:bg-teal-950/60',
      'bg-orange-100/90 dark:bg-orange-950/60',
      'bg-cyan-100/90 dark:bg-cyan-950/60',
      'bg-fuchsia-100/90 dark:bg-fuchsia-950/60'
    ];
    if (idx === -1) return 'bg-slate-100/90 dark:bg-slate-900/60';
    return colors[idx % colors.length];
  };

  const monthlyDateList = useMemo(() => {
    const targetMonthKey = `${selectedYear}-${selectedMonth}`;
    return semesterDateList.filter((d) => d.monthKey === targetMonthKey);
  }, [semesterDateList, selectedMonth, selectedYear]);

  // Sync temp dates & holidays when opening modal
  useEffect(() => {
    if (isSettingSemesterOpen) {
      setTempStartDate(semesterStartDate);
      setTempEndDate(semesterEndDate);
      setTempHolidays([...customHolidays]);
    }
  }, [isSettingSemesterOpen, semesterStartDate, semesterEndDate, customHolidays]);

  const handleAddHoliday = () => {
    if (!newHolidayDate) return;
    if (tempHolidays.some(h => h.date === newHolidayDate)) {
      alert('Tanggal tersebut sudah ditambahkan sebagai hari libur.');
      return;
    }
    const updated = [...tempHolidays, { date: newHolidayDate, description: newHolidayDesc || 'Hari Libur / Cuti Bersama' }].sort((a, b) => a.date.localeCompare(b.date));
    setTempHolidays(updated);
    setNewHolidayDate('');
    setNewHolidayDesc('');
  };

  const handleRemoveHoliday = (date: string) => {
    setTempHolidays(tempHolidays.filter(h => h.date !== date));
  };

  // Handle Save Semester Dates & Holidays with Cloud Firebase Synchronization
  const handleSaveSemesterDates = async () => {
    if (!tempStartDate || !tempEndDate) return;
    if (tempStartDate > tempEndDate) {
      alert('Tanggal Awal Masuk Sekolah tidak boleh melebihi Tanggal Akhir Semester.');
      return;
    }

    setIsSavingSemesterSettings(true);
    setSemesterStartDate(tempStartDate);
    setSemesterEndDate(tempEndDate);
    setCustomHolidays(tempHolidays);

    try {
      localStorage.setItem(`smpn1_semester_start_${homeroomClass}`, tempStartDate);
      localStorage.setItem(`smpn1_semester_end_${homeroomClass}`, tempEndDate);
      localStorage.setItem('smpn1_semester_start_date', tempStartDate);
      localStorage.setItem('smpn1_semester_end_date', tempEndDate);
      localStorage.setItem(`smpn1_custom_holidays_${homeroomClass}`, JSON.stringify(tempHolidays));
      localStorage.setItem('smpn1_custom_holidays', JSON.stringify(tempHolidays));
    } catch (e) {
      console.error(e);
    }

    // Build unified SchoolSettings for cloud persistence
    const updatedSettings: SchoolSettings = {
      ...schoolSettings,
      semesterStartDate: tempStartDate,
      semesterEndDate: tempEndDate,
      customHolidays: tempHolidays,
      classCustomHolidays: {
        ...(schoolSettings?.classCustomHolidays || {}),
        [homeroomClass]: tempHolidays,
      },
      classSemesterDates: {
        ...(schoolSettings?.classSemesterDates || {}),
        [homeroomClass]: {
          startDate: tempStartDate,
          endDate: tempEndDate,
        },
      },
    };

    if (onUpdateSettings) {
      try {
        await onUpdateSettings(updatedSettings);
        setSaveSuccessMsg('Pengaturan semester & hari libur berhasil disimpan ke Firebase!');
        setTimeout(() => {
          setSaveSuccessMsg(null);
          setIsSettingSemesterOpen(false);
        }, 1000);
      } catch (err) {
        console.error('Failed to sync settings to Firebase:', err);
        setIsSettingSemesterOpen(false);
      } finally {
        setIsSavingSemesterSettings(false);
      }
    } else {
      setIsSavingSemesterSettings(false);
      setIsSettingSemesterOpen(false);
    }
  };

  // Preset Handlers for Quick Configuration
  const handleApplyPreset = (type: 'ganjil_current' | 'genap_current' | 'ganjil_next' | 'genap_next') => {
    const y = selectedYear;
    if (type === 'ganjil_current') {
      setTempStartDate(`${y}-07-14`);
      setTempEndDate(`${y}-12-19`);
      setTempSemesterName('Ganjil');
    } else if (type === 'genap_current') {
      setTempStartDate(`${y}-01-05`);
      setTempEndDate(`${y}-06-19`);
      setTempSemesterName('Genap');
    } else if (type === 'ganjil_next') {
      setTempStartDate(`${y + 1}-07-13`);
      setTempEndDate(`${y + 1}-12-18`);
      setTempSemesterName('Ganjil');
    } else if (type === 'genap_next') {
      setTempStartDate(`${y + 1}-01-04`);
      setTempEndDate(`${y + 1}-06-18`);
      setTempSemesterName('Genap');
    }
  };

  // Calculate Semester Effective Working Days (Senin s/d Jumat, excluding custom holidays)
  const semesterTotalWorkingDays = useMemo(() => {
    if (!semesterStartDate || !semesterEndDate) return 0;
    const start = new Date(semesterStartDate);
    const end = new Date(semesterEndDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return 0;

    let count = 0;
    const curr = new Date(start);
    while (curr <= end) {
      const day = curr.getDay();
      const dateStr = curr.toISOString().split('T')[0];
      if (day !== 0 && day !== 6 && !holidaySet.has(dateStr)) {
        count++;
      }
      curr.setDate(curr.getDate() + 1);
    }
    return count;
  }, [semesterStartDate, semesterEndDate, holidaySet]);

  // Calculate Working Days elapsed so far in the semester (up to today or end date)
  const semesterElapsedWorkingDays = useMemo(() => {
    if (!semesterStartDate || !semesterEndDate) return 0;
    const start = new Date(semesterStartDate);
    const end = new Date(semesterEndDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return 0;

    const limit = today < end ? today : end;
    if (limit < start) return 0;

    let count = 0;
    const curr = new Date(start);
    while (curr <= limit) {
      const day = curr.getDay();
      const dateStr = curr.toISOString().split('T')[0];
      if (day !== 0 && day !== 6 && !holidaySet.has(dateStr)) {
        count++;
      }
      curr.setDate(curr.getDate() + 1);
    }
    return count;
  }, [semesterStartDate, semesterEndDate, holidaySet]);

  // Helper to shift selectedDate by offset days
  const shiftDay = (offset: number) => {
    const d = new Date(selectedDate || todayStr);
    if (!isNaN(d.getTime())) {
      d.setDate(d.getDate() + offset);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      setSelectedDate(`${yyyy}-${mm}-${dd}`);
    }
  };

  const handlePrevDay = () => shiftDay(-1);
  const handleNextDay = () => shiftDay(1);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('SEMUA');

  // Filter students belonging to this homeroom class
  const classStudents = useMemo(() => {
    return students.filter((s) => s.class === homeroomClass);
  }, [students, homeroomClass]);

  // Filtered student list by search query
  const filteredStudents = useMemo(() => {
    return classStudents.filter((s) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        s.name.toLowerCase().includes(q) ||
        (s.nis && s.nis.toLowerCase().includes(q)) ||
        (s.nisn && s.nisn.toLowerCase().includes(q));
      return matchesSearch;
    });
  }, [classStudents, searchQuery]);

  // Calculation for Attendance Analysis (Analisis Ketidakhadiran Siswa)
  const analysisResult = useMemo(() => {
    const classStudentIds = new Set(classStudents.map((s) => s.id));
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const todayStrLocal = today.toISOString().split('T')[0];

    // Tentukan rentang tanggal dan daftar hari belajar aktif (Senin - Jumat dan bukan hari libur)
    let activeDates: string[] = [];

    if (analysisScope === 'bulanan') {
      const yearNum = parseInt(analysisSelectedYear.toString(), 10);
      const monthNum = parseInt(analysisSelectedMonth, 10);
      const monthStr = String(monthNum).padStart(2, '0');
      const monthStart = `${yearNum}-${monthStr}-01`;
      const lastDayNum = new Date(yearNum, monthNum, 0).getDate();
      const monthEnd = `${yearNum}-${monthStr}-${String(lastDayNum).padStart(2, '0')}`;

      const effectiveStart = semesterStartDate > monthStart ? semesterStartDate : monthStart;
      const semesterEndLimit = semesterEndDate || monthEnd;
      const effectiveEndBeforeToday = todayStrLocal < semesterEndLimit ? todayStrLocal : semesterEndLimit;
      const effectiveEnd = effectiveEndBeforeToday < monthEnd ? effectiveEndBeforeToday : monthEnd;

      if (effectiveStart <= effectiveEnd) {
        let curr = new Date(effectiveStart);
        const endD = new Date(effectiveEnd);
        while (curr <= endD) {
          const day = curr.getDay();
          if (day !== 0 && day !== 6) {
            const dateStr = curr.toISOString().split('T')[0];
            if (!holidaySet.has(dateStr)) {
              activeDates.push(dateStr);
            }
          }
          curr.setDate(curr.getDate() + 1);
        }
      }
    } else {
      // Seluruh semester sampai hari ini
      const effectiveEnd = semesterEndDate < todayStrLocal ? semesterEndDate : todayStrLocal;
      if (semesterStartDate <= effectiveEnd) {
        let curr = new Date(semesterStartDate);
        const endD = new Date(effectiveEnd);
        while (curr <= endD) {
          const day = curr.getDay();
          if (day !== 0 && day !== 6) {
            const dateStr = curr.toISOString().split('T')[0];
            if (!holidaySet.has(dateStr)) {
              activeDates.push(dateStr);
            }
          }
          curr.setDate(curr.getDate() + 1);
        }
      }
    }

    const activeDatesSet = new Set(activeDates);
    const totalWorkingDays = activeDates.length;

    // Filter records yang masuk dalam rentang hari belajar aktif
    const records = effectiveAttendanceRecords.filter(
      (r) => classStudentIds.has(r.studentId) && activeDatesSet.has(r.date)
    );

    // Map per siswa
    const studentMap = new Map<
      string,
      {
        student: Student;
        sakit: number;
        izin: number;
        alpa: number;
        terlambat: number;
        pulang: number;
        hadir: number;
        totalSIA: number;
        totalRecorded: number;
        percentageHadir: number;
        details: { date: string; status: AttendanceStatus; description?: string }[];
      }
    >();

    // Inisialisasi setiap siswa di kelas
    classStudents.forEach((st) => {
      studentMap.set(st.id, {
        student: st,
        sakit: 0,
        izin: 0,
        alpa: 0,
        terlambat: 0,
        pulang: 0,
        hadir: totalWorkingDays, // Default hadir sejumlah total hari belajar
        totalSIA: 0,
        totalRecorded: totalWorkingDays,
        percentageHadir: 100,
        details: [],
      });
    });

    // Akumulasi records non-hadir dan status khusus
    records.forEach((r) => {
      const entry = studentMap.get(r.studentId);
      if (!entry) return;

      if (r.status === AttendanceStatus.Sakit) {
        entry.sakit += 1;
        entry.totalSIA += 1;
        entry.details.push({ date: r.date, status: r.status, description: r.description });
      } else if (r.status === AttendanceStatus.Izin) {
        entry.izin += 1;
        entry.totalSIA += 1;
        entry.details.push({ date: r.date, status: r.status, description: r.description });
      } else if (r.status === AttendanceStatus.Alpa) {
        entry.alpa += 1;
        entry.totalSIA += 1;
        entry.details.push({ date: r.date, status: r.status, description: r.description });
      } else if (r.status === AttendanceStatus.Terlambat) {
        entry.terlambat += 1;
        entry.details.push({ date: r.date, status: r.status, description: r.description });
      } else if (r.status === AttendanceStatus.Pulang) {
        entry.pulang += 1;
        entry.details.push({ date: r.date, status: r.status, description: r.description });
      }
    });

    // Hitung Hadir = Hari Belajar Efektif - Total SIA (Sakit + Izin + Alpa)
    studentMap.forEach((entry) => {
      entry.hadir = Math.max(0, totalWorkingDays - entry.totalSIA);
      entry.totalRecorded = totalWorkingDays;
      entry.percentageHadir =
        totalWorkingDays > 0
          ? Math.round((entry.hadir / totalWorkingDays) * 100)
          : 100;
      entry.details.sort((a, b) => a.date.localeCompare(b.date));
    });

    const allList = Array.from(studentMap.values());
    const affectedList = allList.filter((item) => item.totalSIA > 0);

    const totalHadir = allList.reduce((acc, curr) => acc + curr.hadir, 0);
    const totalSakit = allList.reduce((acc, curr) => acc + curr.sakit, 0);
    const totalIzin = allList.reduce((acc, curr) => acc + curr.izin, 0);
    const totalAlpa = allList.reduce((acc, curr) => acc + curr.alpa, 0);
    const totalTerlambat = allList.reduce((acc, curr) => acc + curr.terlambat, 0);
    const totalPulang = allList.reduce((acc, curr) => acc + curr.pulang, 0);
    const totalSIAAccum = totalSakit + totalIzin + totalAlpa;
    const totalPossibleAttendance = totalWorkingDays * classStudents.length;
    const classAttendanceRate =
      totalPossibleAttendance > 0
        ? Math.round((totalHadir / totalPossibleAttendance) * 100)
        : 100;

    return {
      allList,
      affectedList,
      totalHadir,
      totalSakit,
      totalIzin,
      totalAlpa,
      totalTerlambat,
      totalPulang,
      totalSIAAccum,
      classAttendanceRate,
      totalAffectedStudents: affectedList.length,
      totalClassStudents: classStudents.length,
      totalWorkingDays,
    };
  }, [analysisScope, analysisSelectedMonth, analysisSelectedYear, effectiveAttendanceRecords, classStudents, semesterStartDate, semesterEndDate, holidaySet]);

  // Filtered and sorted list based on search, filter, and sort options
  const analysisDisplayList = useMemo(() => {
    let list = analysisFilterOnlySIA ? [...analysisResult.affectedList] : [...analysisResult.allList];
    
    if (analysisSearchQuery.trim()) {
      const q = analysisSearchQuery.toLowerCase();
      list = list.filter(
        (item) =>
          item.student.name.toLowerCase().includes(q) ||
          (item.student.nis && item.student.nis.toLowerCase().includes(q))
      );
    }

    // Pengurutan (Sorting)
    if (analysisSortBy === 'sia') {
      list.sort((a, b) => b.totalSIA - a.totalSIA || a.student.name.localeCompare(b.student.name));
    } else if (analysisSortBy === 'nis') {
      list.sort((a, b) => (a.student.nis || '').localeCompare(b.student.nis || ''));
    } else if (analysisSortBy === 'pct') {
      list.sort((a, b) => a.percentageHadir - b.percentageHadir || b.totalSIA - a.totalSIA);
    } else {
      // Default: Nama A-Z
      list.sort((a, b) => a.student.name.localeCompare(b.student.name));
    }

    return list;
  }, [analysisResult, analysisFilterOnlySIA, analysisSearchQuery, analysisSortBy]);

  // Monthly stats breakdown calculation using the formula:
  // Persentase (Sakit/Izin/Alpa) = Jumlah (Sakit/Izin/Alpa) / (Jumlah Murid x Jumlah Hari Belajar (Senin s/d Jumat))
  const monthlyStatsBreakdown = useMemo(() => {
    const yearNum = parseInt(selectedYear.toString(), 10);
    const monthNum = parseInt(selectedMonth, 10);
    const monthStr = String(monthNum).padStart(2, '0');
    
    const monthStart = `${yearNum}-${monthStr}-01`;
    const lastDayNum = new Date(yearNum, monthNum, 0).getDate();
    const monthEnd = `${yearNum}-${monthStr}-${String(lastDayNum).padStart(2, '0')}`;

    const todayStrLocal = new Date().toISOString().split('T')[0];
    const effectiveStart = semesterStartDate > monthStart ? semesterStartDate : monthStart;
    const semesterEndLimit = semesterEndDate || monthEnd;
    const effectiveEndBeforeToday = todayStrLocal < semesterEndLimit ? todayStrLocal : semesterEndLimit;
    const effectiveEnd = effectiveEndBeforeToday < monthEnd ? effectiveEndBeforeToday : monthEnd;

    const classStudentIds = new Set(classStudents.map((s) => s.id));
    let relevantRecords: AttendanceRecord[] = [];
    if (effectiveStart <= effectiveEnd) {
      relevantRecords = effectiveAttendanceRecords.filter(
        (r) => r.date >= effectiveStart && r.date <= effectiveEnd && classStudentIds.has(r.studentId)
      );
    }

    const totalSakit = relevantRecords.filter((r) => r.status === AttendanceStatus.Sakit).length;
    const totalIzin = relevantRecords.filter((r) => r.status === AttendanceStatus.Izin).length;
    const totalAlpa = relevantRecords.filter((r) => r.status === AttendanceStatus.Alpa).length;
    const totalTerlambat = relevantRecords.filter((r) => r.status === AttendanceStatus.Terlambat).length;
    const totalPulang = relevantRecords.filter((r) => r.status === AttendanceStatus.Pulang).length;
    const totalHadirRaw = relevantRecords.filter((r) => r.status === AttendanceStatus.Hadir).length;

    const totalHariBelajar = (() => {
      if (effectiveStart > effectiveEnd) return 0;
      let count = 0;
      const curr = new Date(effectiveStart);
      const endD = new Date(effectiveEnd);
      while (curr <= endD) {
        const day = curr.getDay();
        if (day !== 0 && day !== 6) {
          count++;
        }
        curr.setDate(curr.getDate() + 1);
      }
      return count;
    })();

    const jmlMurid = classStudents.length || 32;
    const denominator = jmlMurid * totalHariBelajar;

    // "H" adalah sisa dari denominator dikurangi (Sakit + Izin + Alpa) karena default siswa adalah Hadir
    const totalHadir = Math.max(0, denominator - (totalSakit + totalIzin + totalAlpa));

    const pctSakit = denominator > 0 ? (totalSakit / denominator) * 100 : 0;
    const pctIzin = denominator > 0 ? (totalIzin / denominator) * 100 : 0;
    const pctAlpa = denominator > 0 ? (totalAlpa / denominator) * 100 : 0;
    const pctHadir = denominator > 0 ? (totalHadir / denominator) * 100 : 0;

    // Tambahan Prosentase Absensi = (sakit + izin + alpa) / (jumlah murid * jumlah hari belajar)
    const totalAbsensi = totalSakit + totalIzin + totalAlpa;
    const pctAbsensi = denominator > 0 ? (totalAbsensi / denominator) * 100 : 0;

    // Prosentase Rata-rata = (prosentase sakit + prosentase izin + prosentase alpa) / 3
    const pctRata2 = (pctSakit + pctIzin + pctAlpa) / 3;

    return {
      totalSakit,
      totalIzin,
      totalAlpa,
      totalHadir,
      totalAbsensi,
      totalHariBelajar,
      jmlMurid,
      pctSakit,
      pctIzin,
      pctAlpa,
      pctHadir,
      pctAbsensi,
      pctRata2,
    };
  }, [selectedYear, selectedMonth, effectiveAttendanceRecords, classStudents]);

  // Week Dates for Mingguan mode
  const currentWeekDates = useMemo(() => {
    return getWeekDates(selectedDate);
  }, [selectedDate]);

  // Helper: Get student attendance record for a specific date (uses effective attendance records)
  const getRecord = (studentId: string, dateStr: string) => {
    return effectiveAttendanceRecords.find((r) => r.studentId === studentId && r.date === dateStr);
  };

  // Helper: Set status for a single student on a date
  const handleSetStatus = (studentId: string, status: AttendanceStatus, dateStr: string = selectedDate, note: string = '') => {
    if (!onUpdateAttendance) return;

    const existingIdx = attendanceRecords.findIndex(
      (r) => r.studentId === studentId && r.date === dateStr
    );
    let updatedRecords = [...attendanceRecords];

    if (existingIdx >= 0) {
      updatedRecords[existingIdx] = {
        ...updatedRecords[existingIdx],
        status,
        description: note !== undefined ? note : updatedRecords[existingIdx].description || '',
        class: homeroomClass,
      };
    } else {
      updatedRecords.push({
        id: `att_${studentId}_${dateStr}_${Date.now()}`,
        studentId,
        date: dateStr,
        status,
        description: note,
        class: homeroomClass,
        teacherId: activeUser?.id,
        agendaId: 'wk_attendance',
      });
    }

    onUpdateAttendance(updatedRecords);

    // Otomatis buat / update Catatan Wali Kelas jika siswa Terlambat
    if (status === AttendanceStatus.Terlambat && onSaveHomeroomNote) {
      const studentObj = classStudents.find((s) => s.id === studentId) || students.find((s) => s.id === studentId);
      const studentName = studentObj ? studentObj.name : 'Siswa';

      const existingNote = homeroomNotes?.find(
        (n) => n.studentId === studentId && n.date === dateStr && (n.category === 'Kedisiplinan' || n.category === 'Kehadiran') && (n.description.toLowerCase().includes('terlambat') || n.description.toLowerCase().includes('keterlambatan'))
      );

      const lateNote: HomeroomJournalNote = {
        id: existingNote ? existingNote.id : `wk_note_late_${studentId}_${dateStr.replace(/-/g, '')}_${Date.now()}`,
        date: dateStr,
        homeroomClass: homeroomClass,
        studentId: studentId,
        studentName: studentName,
        category: 'Kedisiplinan',
        description: `Siswa terlambat hadir pada tanggal ${dateStr} (Absensi Harian Kelas ${homeroomClass}).${note ? ` Keterangan: ${note}` : ''}`,
        actionTaken: 'Telah dicatat otomatis dari absensi harian kelas. Memerlukan pembinaan wali kelas.',
        status: 'Dalam Proses',
        teacherId: activeUser?.id,
        teacherName: activeUser?.name,
        createdAt: existingNote?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      onSaveHomeroomNote(lateNote, existingNote ? 'PUT' : 'POST');
    }
  };

  // Helper: Set description / note for a student
  const handleSetNote = (studentId: string, note: string, dateStr: string = selectedDate) => {
    if (!onUpdateAttendance) return;

    const existing = getRecord(studentId, dateStr);
    const currentStatus = existing?.status || AttendanceStatus.Hadir;

    handleSetStatus(studentId, currentStatus, dateStr, note);
  };

  // Helper: Mark all students as Hadir for selected date
  const handleMarkAllHadir = () => {
    if (!onUpdateAttendance || classStudents.length === 0) return;

    let updatedRecords = [...attendanceRecords];
    classStudents.forEach((std) => {
      const existingIdx = updatedRecords.findIndex(
        (r) => r.studentId === std.id && r.date === selectedDate
      );
      if (existingIdx >= 0) {
        updatedRecords[existingIdx] = {
          ...updatedRecords[existingIdx],
          status: AttendanceStatus.Hadir,
          class: homeroomClass,
        };
      } else {
        updatedRecords.push({
          id: `att_${std.id}_${selectedDate}_${Date.now()}`,
          studentId: std.id,
          date: selectedDate,
          status: AttendanceStatus.Hadir,
          description: '',
          class: homeroomClass,
          teacherId: activeUser?.id,
          agendaId: 'wk_attendance',
        });
      }
    });

    onUpdateAttendance(updatedRecords);
  };

  const getTotalWorkingDays = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (viewMode === 'harian') {
      const d = new Date(selectedDate);
      if (d.getDay() === 0 || d.getDay() === 6) return 0;
      return d <= today ? 1 : 0;
    }
    if (viewMode === 'mingguan') {
      return currentWeekDates.filter((w) => {
        const d = new Date(w.dateStr);
        return d <= today && d.getDay() !== 0 && d.getDay() !== 6;
      }).length;
    }
    if (viewMode === 'bulanan') {
      const yearNum = parseInt(selectedYear.toString(), 10);
      const monthNum = parseInt(selectedMonth, 10);
      const monthStr = String(monthNum).padStart(2, '0');
      const monthStart = `${yearNum}-${monthStr}-01`;
      const lastDayNum = new Date(yearNum, monthNum, 0).getDate();
      const monthEnd = `${yearNum}-${monthStr}-${String(lastDayNum).padStart(2, '0')}`;

      const effectiveStart = semesterStartDate > monthStart ? semesterStartDate : monthStart;
      const semesterEndLimit = semesterEndDate || monthEnd;
      const todayStrLocal = today.toISOString().split('T')[0];
      const effectiveEndBeforeToday = todayStrLocal < semesterEndLimit ? todayStrLocal : semesterEndLimit;
      const effectiveEnd = effectiveEndBeforeToday < monthEnd ? effectiveEndBeforeToday : monthEnd;

      if (effectiveStart > effectiveEnd) return 0;

      let count = 0;
      let curr = new Date(effectiveStart);
      let endD = new Date(effectiveEnd);
      while (curr <= endD) {
        const day = curr.getDay();
        if (day !== 0 && day !== 6) count++;
        curr.setDate(curr.getDate() + 1);
      }
      return count;
    }
    if (viewMode === 'semester') {
      return semesterElapsedWorkingDays || 1;
    }
    if (viewMode === 'tahunan') {
      const year = parseInt(selectedYear.toString());
      let count = 0;
      let d = new Date(year, 0, 1);
      while (d.getFullYear() === year && d <= today) {
        if (d.getDay() !== 0 && d.getDay() !== 6) count++;
        d.setDate(d.getDate() + 1);
      }
      return count;
    }
    return 1;
  };

  // Calculate Rekap Stats depending on active View Mode
  const rekapStats = useMemo(() => {
    let relevantRecords: AttendanceRecord[] = [];

    if (viewMode === 'harian') {
      relevantRecords = effectiveAttendanceRecords.filter((r) => r.date === selectedDate);
    } else if (viewMode === 'mingguan') {
      const weekDatesStr = currentWeekDates.map((w) => w.dateStr);
      relevantRecords = effectiveAttendanceRecords.filter((r) => weekDatesStr.includes(r.date));
    } else if (viewMode === 'bulanan') {
      const yearNum = parseInt(selectedYear.toString(), 10);
      const monthNum = parseInt(selectedMonth, 10);
      const monthStr = String(monthNum).padStart(2, '0');
      const monthStart = `${yearNum}-${monthStr}-01`;
      const lastDayNum = new Date(yearNum, monthNum, 0).getDate();
      const monthEnd = `${yearNum}-${monthStr}-${String(lastDayNum).padStart(2, '0')}`;

      const effectiveStart = semesterStartDate > monthStart ? semesterStartDate : monthStart;
      const semesterEndLimit = semesterEndDate || monthEnd;
      const todayStrLocal = new Date().toISOString().split('T')[0];
      const effectiveEndBeforeToday = todayStrLocal < semesterEndLimit ? todayStrLocal : semesterEndLimit;
      const effectiveEnd = effectiveEndBeforeToday < monthEnd ? effectiveEndBeforeToday : monthEnd;

      if (effectiveStart <= effectiveEnd) {
        relevantRecords = effectiveAttendanceRecords.filter(
          (r) => r.date >= effectiveStart && r.date <= effectiveEnd
        );
      } else {
        relevantRecords = [];
      }
    } else if (viewMode === 'semester') {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const todayStrLocal = today.toISOString().split('T')[0];
      const effectiveEnd = semesterEndDate < todayStrLocal ? semesterEndDate : todayStrLocal;

      relevantRecords = effectiveAttendanceRecords.filter(
        (r) => r.date >= semesterStartDate && r.date <= effectiveEnd
      );
    } else if (viewMode === 'tahunan') {
      relevantRecords = effectiveAttendanceRecords.filter((r) => r.date.startsWith(selectedYear.toString()));
    }

    // Only consider Mon-Fri records for the calculations
    const isWeekday = (dateStr: string) => {
      const d = new Date(dateStr);
      return d.getDay() !== 0 && d.getDay() !== 6;
    };
    relevantRecords = relevantRecords.filter((r) => isWeekday(r.date));

    // Filter for class students only
    const classStudentIds = new Set(classStudents.map((s) => s.id));
    const filteredRecs = relevantRecords.filter((r) => classStudentIds.has(r.studentId));

    const sakit = filteredRecs.filter((r) => r.status === AttendanceStatus.Sakit).length;
    const izin = filteredRecs.filter((r) => r.status === AttendanceStatus.Izin).length;
    const alpa = filteredRecs.filter((r) => r.status === AttendanceStatus.Alpa).length;
    const terlambat = filteredRecs.filter((r) => r.status === AttendanceStatus.Terlambat).length;
    const pulang = filteredRecs.filter((r) => r.status === AttendanceStatus.Pulang).length;

    const workingDaysPerStudent = getTotalWorkingDays();
    const totalRecordsCount = workingDaysPerStudent * classStudents.length;

    // "Hadir" is default for all working days minus (Sakit + Izin + Alpa)
    // Terlambat and Pulang are also counted as Hadir
    const totalSIA = sakit + izin + alpa;
    const hadir = Math.max(0, totalRecordsCount - totalSIA);

    const presentCount = hadir; 
    const percentage =
      totalRecordsCount > 0 ? Math.round((presentCount / totalRecordsCount) * 100) : 100;

    return {
      hadir,
      sakit,
      izin,
      alpa,
      terlambat,
      pulang,
      totalRecordsCount,
      percentage,
    };
  }, [
    viewMode,
    selectedDate,
    currentWeekDates,
    selectedYear,
    selectedMonth,
    semesterStartDate,
    semesterEndDate,
    effectiveAttendanceRecords,
    classStudents,
    semesterElapsedWorkingDays,
  ]);

  // List of students per status for Harian view mode
  const harianStudentsByStatus = useMemo(() => {
    if (viewMode !== 'harian') {
      return { sakit: [], izin: [], alpa: [], terlambat: [], pulang: [] };
    }

    const sakitList: { id: string; name: string; note?: string }[] = [];
    const izinList: { id: string; name: string; note?: string }[] = [];
    const alpaList: { id: string; name: string; note?: string }[] = [];
    const terlambatList: { id: string; name: string; note?: string }[] = [];
    const pulangList: { id: string; name: string; note?: string }[] = [];

    classStudents.forEach((s) => {
      const rec = getRecord(s.id, selectedDate);
      if (!rec) return; // default status is Hadir
      const item = { id: s.id, name: s.name, note: rec.description };
      if (rec.status === AttendanceStatus.Sakit) sakitList.push(item);
      else if (rec.status === AttendanceStatus.Izin) izinList.push(item);
      else if (rec.status === AttendanceStatus.Alpa) alpaList.push(item);
      else if (rec.status === AttendanceStatus.Terlambat) terlambatList.push(item);
      else if (rec.status === AttendanceStatus.Pulang) pulangList.push(item);
    });

    return {
      sakit: sakitList,
      izin: izinList,
      alpa: alpaList,
      terlambat: terlambatList,
      pulang: pulangList,
    };
  }, [viewMode, classStudents, selectedDate, effectiveAttendanceRecords]);

  // Deteksi siswa sakit 3 hari atau lebih berturut-turut dihitung dari hari yang dipilih (selectedDate) ke belakang
  const consecutiveSickWarnings = useMemo(() => {
    // Cari tanggal-tanggal sekolah aktif (bukan Sabtu/Minggu & bukan libur) sampai dengan selectedDate
    // Urutkan dari yang terbaru (selectedDate) mundur ke belakang
    const pastActiveDates: string[] = [];
    const checkDate = new Date(selectedDate);
    // Maksimal kita telusuri 21 hari kalender ke belakang untuk mendapatkan hari-hari sekolah efektif
    for (let i = 0; i < 25; i++) {
      const d = new Date(checkDate);
      d.setDate(d.getDate() - i);
      const day = d.getDay();
      if (day !== 0 && day !== 6) {
        const dateStr = d.toISOString().split('T')[0];
        if (!holidaySet.has(dateStr)) {
          pastActiveDates.push(dateStr);
        }
      }
      if (pastActiveDates.length >= 10) break;
    }

    if (pastActiveDates.length < 3) return [];

    const last3Days = pastActiveDates.slice(0, 3); // [H0 (hari ini), H-1, H-2]

    const warnings: {
      student: Student;
      consecutiveDays: number;
      dates: string[];
      notes: string[];
    }[] = [];

    classStudents.forEach((student) => {
      // Periksa apakah siswa berstatus sakit di ketiga hari terakhir berturut-turut
      let count = 0;
      const sickDates: string[] = [];
      const notes: string[] = [];

      for (const dStr of pastActiveDates) {
        const rec = effectiveAttendanceRecords.find(
          (r) => r.studentId === student.id && r.date === dStr
        );
        if (rec && rec.status === AttendanceStatus.Sakit) {
          count++;
          sickDates.push(dStr);
          if (rec.description) notes.push(`${safeFormatDate(dStr, { day: 'numeric', month: 'short' })}: ${rec.description}`);
        } else {
          // Putus berturut-turut jika bukan sakit
          break;
        }
      }

      // Jika sakit minimal 3 hari berturut-turut dari hari terpilih
      if (count >= 3) {
        warnings.push({
          student,
          consecutiveDays: count,
          dates: sickDates,
          notes,
        });
      }
    });

    return warnings;
  }, [selectedDate, classStudents, effectiveAttendanceRecords, holidaySet]);

  // Set ID siswa yang sakit 3 hari berturut-turut untuk quick lookup di badge tabel
  const consecutiveSickStudentIds = useMemo(() => {
    return new Set(consecutiveSickWarnings.map((w) => w.student.id));
  }, [consecutiveSickWarnings]);

  // Helper: Calculate attendance breakdown per student for a given records subset
  const getStudentBreakdown = (studentId: string, recordsSubset: AttendanceRecord[]) => {
    // Only consider Mon-Fri records
    const isWeekday = (dateStr: string) => {
      const d = new Date(dateStr);
      return d.getDay() !== 0 && d.getDay() !== 6;
    };
    const recs = recordsSubset.filter((r) => r.studentId === studentId && isWeekday(r.date));
    
    const S = recs.filter((r) => r.status === AttendanceStatus.Sakit).length;
    const I = recs.filter((r) => r.status === AttendanceStatus.Izin).length;
    const A = recs.filter((r) => r.status === AttendanceStatus.Alpa).length;
    const T = recs.filter((r) => r.status === AttendanceStatus.Terlambat).length;
    const P = recs.filter((r) => r.status === AttendanceStatus.Pulang).length;
    
    const workingDays = getTotalWorkingDays();
    const Total = workingDays;

    // "Hadir" is default for all working days minus (Sakit + Izin + Alpa)
    const totalSIA = S + I + A;
    const H = Math.max(0, Total - totalSIA);
    
    const Present = H;
    const Pct = Total > 0 ? Math.round((Present / Total) * 100) : 100;

    return { H, S, I, A, T, P, Total, Pct };
  };

  // Print Official Attendance Report
  const handlePrintReport = (format: 'landscape' | 'portrait' | 'bulanan_per_halaman' = 'bulanan_per_halaman') => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    const schoolTitle = schoolSettings?.schoolName || 'SMP NEGERI 1';
    
    const leftLogoHtml = schoolSettings?.departmentLogoUrl
      ? `<img src="${schoolSettings.departmentLogoUrl}" style="height: 48px; max-width: 55px; object-fit: contain;" alt="Logo Dinas" />`
      : (schoolSettings?.logoUrl 
          ? `<img src="${schoolSettings.logoUrl}" style="height: 48px; max-width: 55px; object-fit: contain;" alt="Logo Sekolah" />` 
          : `<img src="https://upload.wikimedia.org/wikipedia/commons/9/9c/Logo_Kementerian_Pendidikan_dan_Kebudayaan.png" style="height: 48px; max-width: 55px; object-fit: contain;" alt="Logo Tut Wuri" />`);

    const rightLogoHtml = schoolSettings?.logoUrl
      ? `<img src="${schoolSettings.logoUrl}" style="height: 48px; max-width: 55px; object-fit: contain;" alt="Logo Sekolah" />`
      : `<img src="https://upload.wikimedia.org/wikipedia/commons/9/9c/Logo_Kementerian_Pendidikan_dan_Kebudayaan.png" style="height: 48px; max-width: 55px; object-fit: contain;" alt="Logo Tut Wuri" />`;

    let modeTitle = '';
    let subtitle = '';

    if (viewMode === 'harian') {
      modeTitle = 'REKAPITULASI KEHADIRAN HARIAN PESERTA DIDIK';
      subtitle = `Hari / Tanggal: ${safeFormatDate(selectedDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`;
    } else if (viewMode === 'mingguan') {
      modeTitle = 'REKAPITULASI KEHADIRAN MINGGUAN PESERTA DIDIK';
      subtitle = `Periode Minggu: ${currentWeekDates[0].formattedDate} s/d ${currentWeekDates[5].formattedDate} (${selectedYear})`;
    } else if (viewMode === 'bulanan') {
      const monthObj = INDONESIAN_MONTHS.find((m) => m.value === selectedMonth);
      modeTitle = 'REKAPITULASI KEHADIRAN BULANAN PESERTA DIDIK';
      subtitle = `Bulan: ${monthObj?.name || selectedMonth} ${selectedYear}`;
    } else if (viewMode === 'semester') {
      modeTitle = 'REKAPITULASI KEHADIRAN SEMESTER PESERTA DIDIK';
      subtitle = `Periode: ${safeFormatDate(semesterStartDate, { day: 'numeric', month: 'long', year: 'numeric' })} s/d ${safeFormatDate(semesterEndDate, { day: 'numeric', month: 'long', year: 'numeric' })} - Semester ${schoolSettings?.semester || 'Ganjil'} T.A. ${schoolSettings?.academicYear || '2025/2026'}`;
    } else if (viewMode === 'tahunan') {
      modeTitle = 'REKAPITULASI KEHADIRAN TAHUNAN PESERTA DIDIK';
      subtitle = `Tahun ${selectedYear} - Tahun Ajaran ${schoolSettings?.academicYear || '2025/2026'}`;
    }

    let bodyHtml = '';
    const isPortrait = format === 'portrait' || format === 'bulanan_per_halaman';

    if (format === 'bulanan_per_halaman') {
      // Ambil daftar bulan unik dari semesterDateList
      let uniqueMonths: string[] = [];
      if (viewMode === 'bulanan') {
        uniqueMonths = [`${selectedYear}-${selectedMonth}`];
      } else {
        uniqueMonths = Array.from(new Set(semesterDateList.map((d) => d.monthKey))).sort();
      }

      bodyHtml = uniqueMonths
        .map((monthKey, mIdx) => {
          const [yearStr, monthStr] = monthKey.split('-');
          const monthObj = INDONESIAN_MONTHS.find((m) => m.value === monthStr);
          const currentMonthTitle = `REKAPITULASI KEHADIRAN BULANAN PESERTA DIDIK`;
          const currentMonthSubtitle = `Bulan: ${monthObj?.name || monthStr} ${yearStr} (Semester ${schoolSettings?.semester || 'Ganjil'} T.A. ${schoolSettings?.academicYear || '2025/2026'})`;

          const monthStart = `${yearStr}-${monthStr}-01`;
          const lastDayNum = new Date(parseInt(yearStr), parseInt(monthStr), 0).getDate();
          const monthEnd = `${yearStr}-${monthStr}-${String(lastDayNum).padStart(2, '0')}`;

          const effectiveStart = semesterStartDate > monthStart ? semesterStartDate : monthStart;
          const semesterEndLimit = semesterEndDate || monthEnd;
          const todayStrLocal = new Date().toISOString().split('T')[0];
          const effectiveEndBeforeToday = todayStrLocal < semesterEndLimit ? todayStrLocal : semesterEndLimit;
          const effectiveEnd = effectiveEndBeforeToday < monthEnd ? effectiveEndBeforeToday : monthEnd;

          // Filter records hanya untuk bulan spesifik ini
          const monthlyRecs = effectiveAttendanceRecords.filter(
            (r) => r.date >= effectiveStart && r.date <= effectiveEnd
          );

          // Ambil tanggal efektif bulan ini dari semesterDateList
          const monthDates = semesterDateList.filter((d) => d.monthKey === monthKey);

          // Generate date headers HTML
          const dateHeadersHtml = monthDates
            .map((d) => {
              const dayNum = d.dateStr.substring(8);
              return `<th style="text-align: center; font-size: 7pt; padding: 1.5px 0.5px; font-weight: bold; width: 1.8%; min-width: 12px;">${parseInt(dayNum)}</th>`;
            })
            .join('');

          // Hitung prosentase bulanan untuk bulan ini (KARTU PROSENTASE)
          const totalSakit = monthlyRecs.filter((r) => r.status === AttendanceStatus.Sakit).length;
          const totalIzin = monthlyRecs.filter((r) => r.status === AttendanceStatus.Izin).length;
          const totalAlpa = monthlyRecs.filter((r) => r.status === AttendanceStatus.Alpa).length;
          const totalTerlambat = monthlyRecs.filter((r) => r.status === AttendanceStatus.Terlambat).length;
          const totalPulang = monthlyRecs.filter((r) => r.status === AttendanceStatus.Pulang).length;
          const totalHadirRaw = monthlyRecs.filter((r) => r.status === AttendanceStatus.Hadir).length;

          const totalHariBelajar = monthDates.length || 1;
          const jmlMurid = classStudents.length || 1;
          const denominator = jmlMurid * totalHariBelajar;

          // "H" adalah sisa dari denominator dikurangi (Sakit + Izin + Alpa) karena default siswa adalah Hadir
          const totalHadir = Math.max(0, denominator - (totalSakit + totalIzin + totalAlpa));
          const totalAbsensi = totalSakit + totalIzin + totalAlpa;

          const pctSakit = denominator > 0 ? (totalSakit / denominator) * 100 : 0;
          const pctIzin = denominator > 0 ? (totalIzin / denominator) * 100 : 0;
          const pctAlpa = denominator > 0 ? (totalAlpa / denominator) * 100 : 0;
          const pctHadir = denominator > 0 ? (totalHadir / denominator) * 100 : 0;
          const pctAbsensi = denominator > 0 ? (totalAbsensi / denominator) * 100 : 0;
          const pctRata2 = (pctSakit + pctIzin + pctAlpa) / 3;

          const rowsHtml = classStudents
            .map((s, idx) => {
              // Hitung breakdown kehadiran untuk siswa ini pada bulan spesifik
              const recs = monthlyRecs.filter((r) => r.studentId === s.id);
              
              const S = recs.filter((r) => r.status === AttendanceStatus.Sakit).length;
              const I = recs.filter((r) => r.status === AttendanceStatus.Izin).length;
              const A = recs.filter((r) => r.status === AttendanceStatus.Alpa).length;
              const T = recs.filter((r) => r.status === AttendanceStatus.Terlambat).length;
              const P = recs.filter((r) => r.status === AttendanceStatus.Pulang).length;
              
              const Total = monthDates.length || 1;
              const totalSIA = S + I + A;
              const H = Math.max(0, Total - totalSIA);
              const Pct = Total > 0 ? Math.round((H / Total) * 100) : 100;

              // Generate date cells HTML
              const dateCellsHtml = monthDates
                .map((d) => {
                  const rec = monthlyRecs.find((r) => r.studentId === s.id && r.date === d.dateStr);
                  const hasSpecialStatus = rec && rec.status && rec.status !== AttendanceStatus.Hadir;
                  const effectiveStatus = hasSpecialStatus ? rec.status : AttendanceStatus.Hadir;

                  let statusChar = 'H';
                  let statusClass = 'color: #16a34a; font-weight: normal;';

                  if (effectiveStatus === AttendanceStatus.Sakit) {
                    statusChar = 'S';
                    statusClass = 'background-color: #0284c7 !important; color: #ffffff !important; font-weight: bold; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;';
                  } else if (effectiveStatus === AttendanceStatus.Izin) {
                    statusChar = 'I';
                    statusClass = 'background-color: #d97706 !important; color: #ffffff !important; font-weight: bold; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;';
                  } else if (effectiveStatus === AttendanceStatus.Alpa) {
                    statusChar = 'A';
                    statusClass = 'background-color: #dc2626 !important; color: #ffffff !important; font-weight: bold; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;';
                  } else if (effectiveStatus === AttendanceStatus.Terlambat) {
                    statusChar = 'T';
                    statusClass = 'background-color: #7c3aed !important; color: #ffffff !important; font-weight: bold; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;';
                  } else if (effectiveStatus === AttendanceStatus.Pulang) {
                    statusChar = 'P';
                    statusClass = 'background-color: #ea580c !important; color: #ffffff !important; font-weight: bold; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;';
                  } else {
                    statusChar = 'H';
                    statusClass = 'color: #16a34a; font-weight: normal;';
                  }

                  return `<td style="text-align: center; border: 1px solid #000; font-size: 7.5pt; padding: 2px 0.5px; ${statusClass}">${statusChar}</td>`;
                })
                .join('');

              const nameColor = s.gender === 'Laki-laki' ? 'color: #0369a1;' : 'color: #be185d;';

              return `
                <tr>
                  <td style="text-align: center;">${idx + 1}</td>
                  <td style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 120px; font-size: 7.5pt; text-align: left; padding: 2px 4px; ${nameColor}"><strong>${s.name}</strong></td>
                  <td style="text-align: center; font-size: 7pt;">${s.gender === 'Laki-laki' ? 'L' : 'P'}</td>
                  ${dateCellsHtml}
                  <td style="text-align: center; font-weight: bold; font-size: 7pt; background-color: #f4fbf7;">${H}</td>
                  <td style="text-align: center; font-weight: bold; font-size: 7pt; background-color: #f5f8ff;">${S}</td>
                  <td style="text-align: center; font-weight: bold; font-size: 7pt; background-color: #fffdf5;">${I}</td>
                  <td style="text-align: center; font-weight: bold; font-size: 7pt; background-color: #fff5f5;">${A}</td>
                  <td style="text-align: center; font-weight: bold; font-size: 7pt; background-color: #faf5ff;">${T}</td>
                  <td style="text-align: center; font-weight: bold; font-size: 7pt; background-color: #fff9f5;">${P}</td>
                  <td style="text-align: center; font-weight: bold; font-size: 7.5pt; background-color: #f8fafc;">${Pct}%</td>
                  <td style="text-align: center; font-size: 7pt;">-</td>
                </tr>
              `;
            })
            .join('');

          const pageBreakStyle = mIdx < uniqueMonths.length - 1 ? 'page-break-after: always; break-after: page;' : '';

          return `
            <div style="${pageBreakStyle} box-sizing: border-box; height: 100%; display: flex; flex-direction: column; justify-content: space-between;">
              <div>
                <!-- KOP SEKOLAH RESMI/BENAR DENGAN LOGO -->
                <div class="kop-container">
                  <div class="kop-logo">
                    ${leftLogoHtml}
                  </div>
                  <div class="kop-text">
                    <div class="kop-gov">${(schoolSettings?.governmentName || 'PEMERINTAH KABUPATEN SUMEDANG').toUpperCase()}</div>
                    <div class="kop-dept">${(schoolSettings?.departmentName || 'DINAS PENDIDIKAN').toUpperCase()}</div>
                    <div class="kop-school">${schoolTitle.toUpperCase()}</div>
                    <div class="kop-address">${schoolSettings?.address || 'Jl. Mayor Abdurahman No. 209, Sumedang Utara, Jawa Barat 45322'}</div>
                  </div>
                  <div class="kop-logo">
                    ${rightLogoHtml}
                  </div>
                </div>

                <!-- JUDUL REKAP SESUAI REQUEST -->
                <div style="text-align: center; margin-bottom: 10px; font-family: 'Times New Roman', Times, serif;">
                  <h2 style="font-size: 10.5pt; font-weight: bold; text-transform: uppercase; margin: 0 0 2px 0; line-height: 1.2;">REKAPITULASI PRESENSI BULANAN & PERSENTASE KEHADIRAN</h2>
                  <h3 style="font-size: 9.5pt; font-weight: bold; text-transform: uppercase; margin: 0; line-height: 1.2;">BULAN: ${(monthObj?.name || monthStr).toUpperCase()} ${yearStr} - KELAS ${homeroomClass.toUpperCase()}</h3>
                </div>

                <table class="info-table">
                  <tr>
                    <td width="50%"><strong>Kelas Perwalian:</strong> ${homeroomClass}</td>
                    <td width="50%" style="text-align: right;"><strong>Wali Kelas:</strong> ${activeUser?.name} (NIP. ${activeUser?.nip || '-'})</td>
                  </tr>
                </table>

                <table class="data" style="width: 100%; border-collapse: collapse;">
                  <thead>
                    <tr>
                      <th rowspan="2" style="width: 3%; font-size: 7.5pt; padding: 2px;">NO</th>
                      <th rowspan="2" style="width: 20%; font-size: 7.5pt; padding: 2px; text-align: left;">NAMA LENGKAP SISWA</th>
                      <th rowspan="2" style="width: 3%; font-size: 7.5pt; padding: 2px;">L/P</th>
                      <th colspan="${monthDates.length}" style="font-size: 7.5pt; padding: 1.5px; background-color: #f8fafc; text-transform: uppercase;">BULAN ${(monthObj?.name || monthStr).toUpperCase()}</th>
                      <th colspan="6" style="font-size: 7.5pt; padding: 1.5px; background-color: #f1f5f9;">REKAP</th>
                      <th rowspan="2" style="width: 5%; font-size: 7.5pt; padding: 2px;">% HADIR</th>
                      <th rowspan="2" style="width: 5%; font-size: 7.5pt; padding: 2px;">KET</th>
                    </tr>
                    <tr>
                      ${dateHeadersHtml}
                      <th style="width: 1.8%; font-size: 7pt; padding: 1px; font-weight: bold; background-color: #16a34a; color: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact;">H</th>
                      <th style="width: 1.8%; font-size: 7pt; padding: 1px; font-weight: bold; background-color: #0284c7; color: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact;">S</th>
                      <th style="width: 1.8%; font-size: 7pt; padding: 1px; font-weight: bold; background-color: #d97706; color: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact;">I</th>
                      <th style="width: 1.8%; font-size: 7pt; padding: 1px; font-weight: bold; background-color: #dc2626; color: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact;">A</th>
                      <th style="width: 1.8%; font-size: 7pt; padding: 1px; font-weight: bold; background-color: #7c3aed; color: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact;">T</th>
                      <th style="width: 1.8%; font-size: 7pt; padding: 1px; font-weight: bold; background-color: #ea580c; color: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact;">P</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${rowsHtml}
                  </tbody>
                </table>

                <!-- KARTU PROSENTASE BULANAN -->
                <div style="margin-top: 10px; margin-bottom: 5px;">
                  <h4 style="font-size: 7.5pt; font-weight: bold; text-transform: uppercase; margin: 0 0 4px 0; font-family: 'Times New Roman', Times, serif; color: #1e293b; letter-spacing: 0.3px;">ANALISIS PERSENTASE BULANAN</h4>
                  <div style="display: flex; gap: 6px; justify-content: space-between; font-family: 'Times New Roman', Times, serif;">
                    <div style="flex: 1; border: 1px solid #10b981; background-color: #f0fdf4; padding: 3px 5px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
                      <div style="font-size: 5.5pt; font-weight: bold; color: #047857; text-transform: uppercase; white-space: nowrap;">PERSENTASE HADIR (H)</div>
                      <div style="font-size: 10pt; font-weight: 900; color: #065f46; margin: 0px 0;">${pctHadir.toFixed(2)}%</div>
                      <div style="font-size: 4.5pt; color: #047857;">Hadir: ${totalHadir} / ${denominator}</div>
                    </div>
                    <div style="flex: 1; border: 1px solid #0284c7; background-color: #f0f9ff; padding: 3px 5px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
                      <div style="font-size: 5.5pt; font-weight: bold; color: #0369a1; text-transform: uppercase; white-space: nowrap;">PERSENTASE SAKIT (S)</div>
                      <div style="font-size: 10pt; font-weight: 900; color: #075985; margin: 0px 0;">${pctSakit.toFixed(2)}%</div>
                      <div style="font-size: 4.5pt; color: #0369a1;">Sakit: ${totalSakit} / ${denominator}</div>
                    </div>
                    <div style="flex: 1; border: 1px solid #d97706; background-color: #fffbeb; padding: 3px 5px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
                      <div style="font-size: 5.5pt; font-weight: bold; color: #b45309; text-transform: uppercase; white-space: nowrap;">PERSENTASE IZIN (I)</div>
                      <div style="font-size: 10pt; font-weight: 900; color: #92400e; margin: 0px 0;">${pctIzin.toFixed(2)}%</div>
                      <div style="font-size: 4.5pt; color: #b45309;">Izin: ${totalIzin} / ${denominator}</div>
                    </div>
                    <div style="flex: 1; border: 1px solid #dc2626; background-color: #fef2f2; padding: 3px 5px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
                      <div style="font-size: 5.5pt; font-weight: bold; color: #b91c1c; text-transform: uppercase; white-space: nowrap;">PERSENTASE ALPA (A)</div>
                      <div style="font-size: 10pt; font-weight: 900; color: #991b1b; margin: 0px 0;">${pctAlpa.toFixed(2)}%</div>
                      <div style="font-size: 4.5pt; color: #b91c1c;">Alpa: ${totalAlpa} / ${denominator}</div>
                    </div>
                    <div style="flex: 1; border: 1px solid #7c3aed; background-color: #f5f3ff; padding: 3px 5px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
                      <div style="font-size: 5.5pt; font-weight: bold; color: #6d28d9; text-transform: uppercase; white-space: nowrap;">PERSENTASE ABSENSI</div>
                      <div style="font-size: 10pt; font-weight: 900; color: #5b21b6; margin: 0px 0;">${pctAbsensi.toFixed(2)}%</div>
                      <div style="font-size: 4.5pt; color: #6d28d9;">Absen: ${totalAbsensi} / ${denominator}</div>
                    </div>
                    <div style="flex: 1; border: 1px solid #4f46e5; background-color: #eef2ff; padding: 3px 5px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
                      <div style="font-size: 5.5pt; font-weight: bold; color: #4338ca; text-transform: uppercase; white-space: nowrap;">PROSENTASE RATA2</div>
                      <div style="font-size: 10pt; font-weight: 900; color: #3730a3; margin: 0px 0;">${pctRata2.toFixed(2)}%</div>
                      <div style="font-size: 4.5pt; color: #4338ca;">(S + I + A) / 3</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- TANPA TTD KEPALA SEKOLAH SESUAI REQUEST -->
              <table class="footer-sig" style="margin-top: 10px;">
                <tr>
                  <td width="50%"></td>
                  <td width="50%">
                    Kawali, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                    Wali Kelas ${homeroomClass}
                    <div class="sig-space" style="height: 25px;"></div>
                    <strong>${activeUser?.name}</strong><br/>
                    NIP. ${activeUser?.nip || '................................'}
                  </td>
                </tr>
              </table>
            </div>
          `;
        })
        .join('');
    } else {
      let printRecords: AttendanceRecord[] = [];
      if (viewMode === 'harian') {
        printRecords = effectiveAttendanceRecords.filter((r) => r.date === selectedDate);
      } else if (viewMode === 'mingguan') {
        const weekDatesStr = currentWeekDates.map((w) => w.dateStr);
        printRecords = effectiveAttendanceRecords.filter((r) => weekDatesStr.includes(r.date));
      } else if (viewMode === 'bulanan') {
        const yearNum = parseInt(selectedYear.toString(), 10);
        const monthNum = parseInt(selectedMonth, 10);
        const monthStr = String(monthNum).padStart(2, '0');
        const monthStart = `${yearNum}-${monthStr}-01`;
        const lastDayNum = new Date(yearNum, monthNum, 0).getDate();
        const monthEnd = `${yearNum}-${monthStr}-${String(lastDayNum).padStart(2, '0')}`;

        const effectiveStart = semesterStartDate > monthStart ? semesterStartDate : monthStart;
        const semesterEndLimit = semesterEndDate || monthEnd;
        const todayStrLocal = new Date().toISOString().split('T')[0];
        const effectiveEndBeforeToday = todayStrLocal < semesterEndLimit ? todayStrLocal : semesterEndLimit;
        const effectiveEnd = effectiveEndBeforeToday < monthEnd ? effectiveEndBeforeToday : monthEnd;

        printRecords = effectiveAttendanceRecords.filter((r) => r.date >= effectiveStart && r.date <= effectiveEnd);
      } else if (viewMode === 'semester') {
        printRecords = effectiveAttendanceRecords.filter((r) => r.date >= semesterStartDate && r.date <= semesterEndDate);
      } else if (viewMode === 'tahunan') {
        printRecords = effectiveAttendanceRecords.filter((r) => r.date.startsWith(selectedYear.toString()));
      }

      // Hitung statistik keseluruhan untuk format lainnya (KARTU PROSENTASE)
      const totalSakit = printRecords.filter((r) => r.status === AttendanceStatus.Sakit).length;
      const totalIzin = printRecords.filter((r) => r.status === AttendanceStatus.Izin).length;
      const totalAlpa = printRecords.filter((r) => r.status === AttendanceStatus.Alpa).length;
      const totalTerlambat = printRecords.filter((r) => r.status === AttendanceStatus.Terlambat).length;
      const totalPulang = printRecords.filter((r) => r.status === AttendanceStatus.Pulang).length;
      const totalHadirRaw = printRecords.filter((r) => r.status === AttendanceStatus.Hadir).length;

      const uniqueDates = Array.from(new Set(printRecords.map((r) => r.date)));
      const totalHariBelajar = uniqueDates.length || 1;
      const jmlMurid = classStudents.length || 1;
      const denominator = jmlMurid * totalHariBelajar;

      // "H" adalah sisa dari denominator dikurangi (Sakit + Izin + Alpa) karena default siswa adalah Hadir
      const totalHadir = Math.max(0, denominator - (totalSakit + totalIzin + totalAlpa));
      const totalAbsensi = totalSakit + totalIzin + totalAlpa;

      const pctSakit = denominator > 0 ? (totalSakit / denominator) * 100 : 0;
      const pctIzin = denominator > 0 ? (totalIzin / denominator) * 100 : 0;
      const pctAlpa = denominator > 0 ? (totalAlpa / denominator) * 100 : 0;
      const pctHadir = denominator > 0 ? (totalHadir / denominator) * 100 : 0;
      const pctAbsensi = denominator > 0 ? (totalAbsensi / denominator) * 100 : 0;
      const pctRata2 = (pctSakit + pctIzin + pctAlpa) / 3;

      const rowsHtml = classStudents
        .map((s, idx) => {
          const bd = getStudentBreakdown(s.id, printRecords);
          const singleRec = viewMode === 'harian' ? getRecord(s.id, selectedDate) : null;
          const printStatus = viewMode === 'harian' ? (singleRec?.status || 'Hadir') : '';
          const noteStr = singleRec?.description ? ` (${singleRec.description})` : '';

          return `
            <tr>
              <td style="text-align: center;">${idx + 1}</td>
              <td><strong>${s.name}</strong></td>
              <td style="text-align: center;">${s.gender === 'Laki-laki' ? 'L' : 'P'}</td>
              <td style="text-align: center;">${bd.H}</td>
              <td style="text-align: center;">${bd.S}</td>
              <td style="text-align: center;">${bd.I}</td>
              <td style="text-align: center;">${bd.A}</td>
              <td style="text-align: center;">${bd.T}</td>
              <td style="text-align: center;">${bd.P}</td>
              <td style="text-align: center; font-weight: bold;">${bd.Pct}%</td>
              <td>${viewMode === 'harian' ? printStatus + noteStr : '-'}</td>
            </tr>
          `;
        })
        .join('');

      bodyHtml = `
        <!-- KOP SEKOLAH RESMI/BENAR DENGAN LOGO -->
        <div class="kop-container">
          <div class="kop-logo">
            ${leftLogoHtml}
          </div>
          <div class="kop-text">
            <div class="kop-gov">${(schoolSettings?.governmentName || 'PEMERINTAH KABUPATEN SUMEDANG').toUpperCase()}</div>
            <div class="kop-dept">${(schoolSettings?.departmentName || 'DINAS PENDIDIKAN').toUpperCase()}</div>
            <div class="kop-school">${schoolTitle.toUpperCase()}</div>
            <div class="kop-address">${schoolSettings?.address || 'Jl. Mayor Abdurahman No. 209, Sumedang Utara, Jawa Barat 45322'}</div>
          </div>
          <div class="kop-logo">
            ${rightLogoHtml}
          </div>
        </div>

        <!-- JUDUL REKAP SESUAI REQUEST -->
        <div style="text-align: center; margin-bottom: 10px; font-family: 'Times New Roman', Times, serif;">
          <h2 style="font-size: 10.5pt; font-weight: bold; text-transform: uppercase; margin: 0 0 2px 0; line-height: 1.2;">REKAPITULASI PRESENSI & PERSENTASE KEHADIRAN</h2>
          <h3 style="font-size: 9.5pt; font-weight: bold; text-transform: uppercase; margin: 0; line-height: 1.2;">${modeTitle} - KELAS ${homeroomClass.toUpperCase()}</h3>
          <p style="font-size: 8.5pt; margin: 2px 0 0 0; font-family: 'Times New Roman', Times, serif;">${subtitle}</p>
        </div>

        <table class="info-table">
          <tr>
            <td width="50%"><strong>Kelas Perwalian:</strong> ${homeroomClass}</td>
            <td width="50%" style="text-align: right;"><strong>Wali Kelas:</strong> ${activeUser?.name} (NIP. ${activeUser?.nip || '-'})</td>
          </tr>
        </table>

        <table class="data">
          <thead>
            <tr>
              <th width="5%">NO</th>
              <th width="41%">NAMA LENGKAP SISWA</th>
              <th width="8%">L/P</th>
              <th width="6%">H</th>
              <th width="6%">S</th>
              <th width="6%">I</th>
              <th width="6%">A</th>
              <th width="6%">T</th>
              <th width="6%">P</th>
              <th width="10%">% HADIR</th>
              <th width="10%">KET</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <!-- KARTU PROSENTASE (FORMAT UMUM) -->
        <div style="margin-top: 15px; margin-bottom: 10px;">
          <h4 style="font-size: 8pt; font-weight: bold; text-transform: uppercase; margin: 0 0 5px 0; font-family: 'Times New Roman', Times, serif; color: #1e293b; letter-spacing: 0.3px;">ANALISIS PERSENTASE PRESTASI</h4>
          <div style="display: flex; gap: 6px; justify-content: space-between; font-family: 'Times New Roman', Times, serif;">
            <div style="flex: 1; border: 1px solid #10b981; background-color: #f0fdf4; padding: 4px 6px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
              <div style="font-size: 5.5pt; font-weight: bold; color: #047857; text-transform: uppercase; white-space: nowrap;">PERSENTASE HADIR (H)</div>
              <div style="font-size: 10.5pt; font-weight: 900; color: #065f46; margin: 2px 0;">${pctHadir.toFixed(2)}%</div>
              <div style="font-size: 4.5pt; color: #047857;">Hadir: ${totalHadir} / ${denominator}</div>
            </div>
            <div style="flex: 1; border: 1px solid #0284c7; background-color: #f0f9ff; padding: 4px 6px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
              <div style="font-size: 5.5pt; font-weight: bold; color: #0369a1; text-transform: uppercase; white-space: nowrap;">PERSENTASE SAKIT (S)</div>
              <div style="font-size: 10.5pt; font-weight: 900; color: #075985; margin: 2px 0;">${pctSakit.toFixed(2)}%</div>
              <div style="font-size: 4.5pt; color: #0369a1;">Sakit: ${totalSakit} / ${denominator}</div>
            </div>
            <div style="flex: 1; border: 1px solid #d97706; background-color: #fffbeb; padding: 4px 6px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
              <div style="font-size: 5.5pt; font-weight: bold; color: #b45309; text-transform: uppercase; white-space: nowrap;">PERSENTASE IZIN (I)</div>
              <div style="font-size: 10.5pt; font-weight: 900; color: #92400e; margin: 2px 0;">${pctIzin.toFixed(2)}%</div>
              <div style="font-size: 4.5pt; color: #b45309;">Izin: ${totalIzin} / ${denominator}</div>
            </div>
            <div style="flex: 1; border: 1px solid #dc2626; background-color: #fef2f2; padding: 4px 6px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
              <div style="font-size: 5.5pt; font-weight: bold; color: #b91c1c; text-transform: uppercase; white-space: nowrap;">PERSENTASE ALPA (A)</div>
              <div style="font-size: 10.5pt; font-weight: 900; color: #991b1b; margin: 2px 0;">${pctAlpa.toFixed(2)}%</div>
              <div style="font-size: 4.5pt; color: #b91c1c;">Alpa: ${totalAlpa} / ${denominator}</div>
            </div>
            <div style="flex: 1; border: 1px solid #7c3aed; background-color: #f5f3ff; padding: 4px 6px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
              <div style="font-size: 5.5pt; font-weight: bold; color: #6d28d9; text-transform: uppercase; white-space: nowrap;">PERSENTASE ABSENSI</div>
              <div style="font-size: 10.5pt; font-weight: 900; color: #5b21b6; margin: 2px 0;">${pctAbsensi.toFixed(2)}%</div>
              <div style="font-size: 4.5pt; color: #6d28d9;">Absen: ${totalAbsensi} / ${denominator}</div>
            </div>
            <div style="flex: 1; border: 1px solid #4f46e5; background-color: #eef2ff; padding: 4px 6px; border-radius: 4px; text-align: center; box-shadow: 0 1px 2px rgba(0,0,0,0.02);">
              <div style="font-size: 5.5pt; font-weight: bold; color: #4338ca; text-transform: uppercase; white-space: nowrap;">PROSENTASE RATA2</div>
              <div style="font-size: 10.5pt; font-weight: 900; color: #3730a3; margin: 2px 0;">${pctRata2.toFixed(2)}%</div>
              <div style="font-size: 4.5pt; color: #4338ca;">(S + I + A) / 3</div>
            </div>
          </div>
        </div>

        <!-- TANPA TTD KEPALA SEKOLAH SESUAI REQUEST -->
        <table class="footer-sig" style="margin-top: 15px;">
          <tr>
            <td width="50%"></td>
            <td width="50%">
              Kawali, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
              Wali Kelas ${homeroomClass}
              <div class="sig-space" style="height: 28px;"></div>
              <strong>${activeUser?.name}</strong><br/>
              NIP. ${activeUser?.nip || '................................'}
            </td>
          </tr>
        </table>
      `;
    }

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${modeTitle} - KELAS ${homeroomClass}</title>
          <style>
            @page { 
              size: A4 ${isPortrait ? 'portrait' : 'landscape'}; 
              margin: 6mm 8mm; 
            }
            @media print {
              body, td, th { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
            }
            * { box-sizing: border-box; }
            body { font-family: 'Times New Roman', Times, serif; color: #000; font-size: 8.5pt; line-height: 1.25; margin: 0; padding: 0; }
            
            /* KOP SEKOLAH RESMI GANDA BORDER */
            .kop-container {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding-bottom: 4px;
              margin-bottom: 8px;
              border-bottom: 2.5px double #000;
              font-family: 'Times New Roman', Times, serif;
            }
            .kop-logo {
              width: 55px;
              height: 48px;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
            .kop-logo img {
              height: 48px;
              max-width: 55px;
              object-fit: contain;
            }
            .kop-text {
              flex: 1;
              text-align: center;
              padding: 0 10px;
              font-family: 'Times New Roman', Times, serif;
            }
            .kop-gov { font-size: 10.5pt; font-weight: bold; text-transform: uppercase; margin: 0; line-height: 1.25; color: #000; }
            .kop-dept { font-size: 10.5pt; font-weight: bold; text-transform: uppercase; margin: 0; line-height: 1.25; color: #000; }
            .kop-school { font-size: 13.5pt; font-weight: bold; text-transform: uppercase; margin: 1px 0 0 0; line-height: 1.25; letter-spacing: 0.5px; color: #000; }
            .kop-address { font-size: 8pt; font-style: italic; margin: 2px 0 0 0; line-height: 1.25; color: #000; }

            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 4px; margin-bottom: 8px; }
            .header h1 { font-size: 11.5pt; margin: 0; text-transform: uppercase; font-weight: bold; }
            .header h2 { font-size: 9.5pt; margin: 2px 0 0 0; text-transform: uppercase; font-weight: bold; }
            .header p { font-size: 8pt; margin: 1px 0 0 0; }
            .info-table { width: 100%; margin-bottom: 6px; font-size: 8.5pt; }
            table.data { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 8pt; }
            table.data th, table.data td { border: 1px solid #000; padding: 2.5px 4px; }
            table.data th { background-color: #f2f2f2; text-align: center; font-weight: bold; }
            .footer-sig { margin-top: 15px; width: 100%; font-size: 8.5pt; page-break-inside: avoid; }
            .footer-sig td { border: none; text-align: center; vertical-align: top; }
            .sig-space { height: 32px; }
            .page-break {
              page-break-after: always;
              break-after: page;
            }
          </style>
        </head>
        <body>
          ${bodyHtml}
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `;

    printWin.document.write(content);
    printWin.document.close();
  };

  // Handler Cetak Laporan Analisis Ketidakhadiran Siswa
  const handlePrintAnalysisReport = () => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    const schoolName = schoolSettings?.schoolName || 'SMP NEGERI CONTOH';
    const logoUrl = schoolSettings?.logoUrl || '';
    const periodLabel =
      analysisScope === 'bulanan'
        ? `BULAN ${INDONESIAN_MONTHS.find((m) => m.value === analysisSelectedMonth)?.name.toUpperCase()} ${analysisSelectedYear}`
        : `SELURUH DATA ABSENSI KELAS (AKUMULATIF)`;

    const printList = analysisDisplayList;

    const rowsHtml = printList
      .map((item, idx) => {
        const detailTags = item.details
          .map((d) => {
            const char =
              d.status === AttendanceStatus.Sakit
                ? 'S'
                : d.status === AttendanceStatus.Izin
                ? 'I'
                : d.status === AttendanceStatus.Alpa
                ? 'A'
                : d.status === AttendanceStatus.Terlambat
                ? 'T'
                : 'P';
            const color =
              d.status === AttendanceStatus.Sakit
                ? '#0284c7'
                : d.status === AttendanceStatus.Izin
                ? '#d97706'
                : d.status === AttendanceStatus.Alpa
                ? '#dc2626'
                : d.status === AttendanceStatus.Terlambat
                ? '#7c3aed'
                : '#ea580c';
            return `<span style="display:inline-block; font-size:7pt; border:1px solid #cbd5e1; border-radius:3px; padding:1px 3px; margin:1px 2px; white-space:nowrap;">
              <strong style="color:${color};">[${char}]</strong> ${new Date(d.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}${d.description ? ` (${d.description})` : ''}
            </span>`;
          })
          .join(' ');

        return `
          <tr>
            <td style="text-align: center;">${idx + 1}</td>
            <td style="text-align: center;">${item.student.nis || '-'}</td>
            <td style="text-align: left; font-weight: bold;">${item.student.name}</td>
            <td style="text-align: center; font-weight: bold; color: #16a34a;">${item.hadir > 0 ? item.hadir : '-'}</td>
            <td style="text-align: center; font-weight: bold; color: #0284c7;">${item.sakit > 0 ? item.sakit : '-'}</td>
            <td style="text-align: center; font-weight: bold; color: #d97706;">${item.izin > 0 ? item.izin : '-'}</td>
            <td style="text-align: center; font-weight: bold; color: #dc2626;">${item.alpa > 0 ? item.alpa : '-'}</td>
            <td style="text-align: center; color: #7c3aed;">${item.terlambat > 0 ? item.terlambat : '-'}</td>
            <td style="text-align: center; color: #ea580c;">${item.pulang > 0 ? item.pulang : '-'}</td>
            <td style="text-align: center; font-weight: 900; background: #fef2f2; color: #991b1b;">${item.totalSIA}</td>
            <td style="text-align: center; font-weight: bold;">${item.percentageHadir}%</td>
            <td style="text-align: left; font-size: 7.5pt;">${detailTags || '<span style="color:#16a34a;">Hadir Lengkap</span>'}</td>
          </tr>
        `;
      })
      .join('');

    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>ANALISIS KETIDAKHADIRAN SISWA - KELAS ${homeroomClass}</title>
          <style>
            @page { 
              size: A4 portrait; 
              margin: 8mm 10mm; 
            }
            @media print {
              body, td, th { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
            }
            * { box-sizing: border-box; }
            body { font-family: 'Times New Roman', Times, serif; color: #000; font-size: 8.5pt; line-height: 1.3; margin: 0; padding: 0; }
            
            .kop-container {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding-bottom: 4px;
              margin-bottom: 8px;
              border-bottom: 2.5px double #000;
              font-family: 'Times New Roman', Times, serif;
            }
            .kop-logo {
              width: 55px;
              height: 48px;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
            .kop-logo img {
              height: 48px;
              max-width: 55px;
              object-fit: contain;
            }
            .kop-text {
              flex: 1;
              text-align: center;
              padding: 0 10px;
            }
            .kop-gov { font-size: 10pt; font-weight: bold; text-transform: uppercase; margin: 0; line-height: 1.2; color: #000; }
            .kop-dept { font-size: 10pt; font-weight: bold; text-transform: uppercase; margin: 0; line-height: 1.2; color: #000; }
            .kop-school { font-size: 13pt; font-weight: 900; text-transform: uppercase; margin: 2px 0 1px 0; color: #000; }
            .kop-addr { font-size: 7.5pt; font-style: italic; margin: 0; color: #000; }
            
            .report-title {
              text-align: center;
              font-size: 11pt;
              font-weight: bold;
              text-transform: uppercase;
              margin: 10px 0 2px 0;
              letter-spacing: 0.5px;
            }
            .report-subtitle {
              text-align: center;
              font-size: 9pt;
              font-weight: bold;
              margin-bottom: 12px;
            }

            .summary-box {
              display: flex;
              gap: 8px;
              margin-bottom: 12px;
            }
            .sum-item {
              flex: 1;
              border: 1px solid #94a3b8;
              border-radius: 4px;
              padding: 4px;
              text-align: center;
              font-size: 8pt;
            }
            .sum-item strong {
              display: block;
              font-size: 11pt;
              margin-top: 2px;
            }

            table.data-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 15px;
            }
            table.data-table th, table.data-table td {
              border: 1px solid #475569;
              padding: 4px 6px;
            }
            table.data-table th {
              background-color: #f1f5f9;
              font-weight: bold;
              text-align: center;
              font-size: 8.5pt;
            }

            table.footer-sig {
              width: 100%;
              border: none;
              font-family: 'Times New Roman', Times, serif;
              font-size: 8.5pt;
            }
            table.footer-sig td {
              border: none;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="kop-container">
            <div class="kop-logo">
              ${logoUrl ? `<img src="${logoUrl}" alt="Logo Sekolah" />` : ''}
            </div>
            <div class="kop-text">
              <div class="kop-gov">${schoolSettings?.governmentName || 'PEMERINTAH DAERAH PROVINSI / KABUPATEN'}</div>
              <div class="kop-dept">${schoolSettings?.departmentName || 'DINAS PENDIDIKAN DAN KEBUDAYAAN'}</div>
              <div class="kop-school">${schoolName}</div>
              <div class="kop-addr">${schoolSettings?.address || 'Jl. Raya Pendidikan No. 1'}${schoolSettings?.additionalKopLine ? ` • ${schoolSettings.additionalKopLine}` : ''}</div>
            </div>
            <div class="kop-logo" style="visibility: hidden;"></div>
          </div>

          <div class="report-title">LAPORAN ANALISIS KETIDAKHADIRAN SISWA</div>
          <div class="report-subtitle">
            KELAS ${homeroomClass} • PERIODE: ${periodLabel}
          </div>

          <div class="summary-box">
            <div class="sum-item" style="background:#f8fafc;">
              <span>Total Siswa</span>
              <strong>${analysisResult.totalClassStudents} Siswa</strong>
            </div>
            <div class="sum-item" style="background:#f0fdf4; border-color:#16a34a; color:#15803d;">
              <span>Total Hadir (H)</span>
              <strong>${analysisResult.totalHadir} Hari</strong>
            </div>
            <div class="sum-item" style="background:#f0f9ff; border-color:#0284c7; color:#0369a1;">
              <span>Total Sakit (S)</span>
              <strong>${analysisResult.totalSakit} Hari</strong>
            </div>
            <div class="sum-item" style="background:#fffbeb; border-color:#d97706; color:#b45309;">
              <span>Total Izin (I)</span>
              <strong>${analysisResult.totalIzin} Hari</strong>
            </div>
            <div class="sum-item" style="background:#fef2f2; border-color:#dc2626; color:#b91c1c;">
              <span>Total Alpa (A)</span>
              <strong>${analysisResult.totalAlpa} Hari</strong>
            </div>
            <div class="sum-item" style="background:#fef2f2; border-color:#991b1b; color:#7f1d1d;">
              <span>Akumulasi SIA</span>
              <strong>${analysisResult.totalSIAAccum} Hari</strong>
            </div>
          </div>

          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 28px;">No</th>
                <th style="width: 65px;">NIS</th>
                <th>Nama Peserta Didik</th>
                <th style="width: 38px;">H</th>
                <th style="width: 38px;">S</th>
                <th style="width: 38px;">I</th>
                <th style="width: 38px;">A</th>
                <th style="width: 35px;">T</th>
                <th style="width: 35px;">P</th>
                <th style="width: 50px;">Total SIA</th>
                <th style="width: 45px;">% Hadir</th>
                <th>Rincian Tanggal & Catatan</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml || '<tr><td colspan="12" style="text-align:center; padding:15px;">Tidak ada data pada periode ini.</td></tr>'}
            </tbody>
          </table>

          <table class="footer-sig" style="margin-top: 20px;">
            <tr>
              <td width="50%"></td>
              <td width="50%">
                ${schoolSettings?.kabupaten || schoolSettings?.kecamatan || 'Kawali'}, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                Wali Kelas ${homeroomClass}
                <div style="height: 35px;"></div>
                <strong>${activeUser?.name}</strong><br/>
                NIP. ${activeUser?.nip || '................................'}
              </td>
            </tr>
          </table>

          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `;

    printWin.document.write(content);
    printWin.document.close();
  };

  return (
    <div className="space-y-6">
      {/* Navigation Header for Tampilan Kehadiran */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded-xl">
            <CalendarDays size={22} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <h3 className="font-bold text-slate-800 dark:text-white text-base">
                Kehadiran Peserta Didik Kelas {homeroomClass}
              </h3>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-black bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300">
                Wali Kelas
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Rekapitulasi lengkap kehadiran siswa (Hadir, Sakit, Izin, Alpa, Terlambat, Pulang) per rentang waktu.
            </p>
          </div>
        </div>

        {/* View Mode Tabs: Harian -> Mingguan -> Bulanan -> Semester -> Tahunan */}
        <div className="flex flex-wrap items-center gap-1.5 bg-slate-100 dark:bg-slate-900/80 p-1.5 rounded-xl border border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setViewMode('harian')}
            className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'harian'
                ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow font-extrabold`
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
            }`}
          >
            📌 Harian
          </button>
          <button
            onClick={() => setViewMode('mingguan')}
            className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'mingguan'
                ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow font-extrabold`
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
            }`}
          >
            🗓️ Mingguan
          </button>
          <button
            onClick={() => setViewMode('bulanan')}
            className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'bulanan'
                ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow font-extrabold`
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
            }`}
          >
            📊 Bulanan
          </button>
          <button
            onClick={() => setViewMode('semester')}
            className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'semester'
                ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow font-extrabold`
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
            }`}
          >
            🎓 Semester
          </button>
          <button
            onClick={() => setViewMode('tahunan')}
            className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'tahunan'
                ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow font-extrabold`
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
            }`}
          >
            📅 Tahunan
          </button>
        </div>
      </div>

      {/* Active Semester Period Banner & Setup Button */}
      <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-blue-50 dark:from-emerald-950/40 dark:via-teal-950/30 dark:to-blue-950/40 border border-emerald-200 dark:border-emerald-800/60 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-600 text-white rounded-xl shadow-xs shrink-0">
            <CalendarRange size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-black uppercase text-emerald-800 dark:text-emerald-300">
                Periode Semester {schoolSettings?.semester || 'Ganjil'} (T.A. {schoolSettings?.academicYear || '2025/2026'})
              </span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-200/80 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-200">
                {semesterTotalWorkingDays} Hari Efektif Belajar
              </span>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
              <strong>Awal Masuk Sekolah:</strong> {safeFormatDate(semesterStartDate, { day: 'numeric', month: 'short', year: 'numeric' })} &nbsp;•&nbsp; 
              <strong>Akhir Semester:</strong> {safeFormatDate(semesterEndDate, { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>

        <button
          onClick={() => setIsSettingSemesterOpen(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white dark:bg-slate-800 hover:bg-emerald-600 hover:text-white dark:hover:bg-emerald-600 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 text-xs font-bold rounded-xl shadow-xs transition-all shrink-0 cursor-pointer"
        >
          <Sliders size={14} />
          <span>Set Awal Masuk & Akhir Semester</span>
        </button>
      </div>

      {/* Rekap Kehadiran Stats Summary Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        {/* Hadir (H) */}
        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-emerald-200 dark:border-emerald-900/50 shadow-sm flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400">
              Hadir (H)
            </div>
            <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
              {rekapStats.hadir}
            </div>
          </div>
          <div className="p-2 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-lg shrink-0">
            <CheckCircle2 size={18} />
          </div>
        </div>

        {/* Sakit (S) */}
        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-blue-200 dark:border-blue-900/50 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400">
                Sakit (S)
              </div>
              <div className="text-xl font-black text-blue-600 dark:text-blue-400 mt-0.5">
                {rekapStats.sakit}
              </div>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 rounded-lg shrink-0">
              <Heart size={18} />
            </div>
          </div>
          {viewMode === 'harian' && (
            <div className="mt-2.5 pt-2 border-t border-blue-100 dark:border-blue-900/40">
              {harianStudentsByStatus.sakit.length > 0 ? (
                <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                  <div className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-0.5">Daftar Siswa Sakit:</div>
                  {harianStudentsByStatus.sakit.map((st) => {
                    const isWarn = consecutiveSickStudentIds.has(st.id);
                    return (
                      <div key={st.id} className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-start gap-1">
                        <span className="text-blue-500 font-bold shrink-0">•</span>
                        <span className="leading-tight">
                          {st.name}
                          {isWarn && (
                            <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 text-[10px] font-black border border-rose-200 dark:border-rose-900" title="Sakit 3 hari berturut-turut!">
                              <AlertTriangle size={10} className="text-rose-600 dark:text-rose-400" />
                              ≥3 Hari Berturut-turut
                            </span>
                          )}
                          {st.note && <span className="block text-[10px] text-blue-600/80 dark:text-blue-400/80 font-normal italic">({st.note})</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[10px] text-slate-400 dark:text-slate-500 italic">Nihil (0 Siswa)</div>
              )}
            </div>
          )}
        </div>

        {/* Izin (I) */}
        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-amber-200 dark:border-amber-900/50 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400">
                Izin (I)
              </div>
              <div className="text-xl font-black text-amber-600 dark:text-amber-400 mt-0.5">
                {rekapStats.izin}
              </div>
            </div>
            <div className="p-2 bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 rounded-lg shrink-0">
              <FileText size={18} />
            </div>
          </div>
          {viewMode === 'harian' && (
            <div className="mt-2.5 pt-2 border-t border-amber-100 dark:border-amber-900/40">
              {harianStudentsByStatus.izin.length > 0 ? (
                <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                  <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-0.5">Daftar Siswa Izin:</div>
                  {harianStudentsByStatus.izin.map((st) => (
                    <div key={st.id} className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-start gap-1">
                      <span className="text-amber-500 font-bold shrink-0">•</span>
                      <span className="leading-tight">
                        {st.name}
                        {st.note && <span className="block text-[10px] text-amber-600/80 dark:text-amber-400/80 font-normal italic">({st.note})</span>}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[10px] text-slate-400 dark:text-slate-500 italic">Nihil (0 Siswa)</div>
              )}
            </div>
          )}
        </div>

        {/* Alpa (A) */}
        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-rose-200 dark:border-rose-900/50 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400">
                Alpa (A)
              </div>
              <div className="text-xl font-black text-rose-600 dark:text-rose-400 mt-0.5">
                {rekapStats.alpa}
              </div>
            </div>
            <div className="p-2 bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 rounded-lg shrink-0">
              <XCircle size={18} />
            </div>
          </div>
          {viewMode === 'harian' && (
            <div className="mt-2.5 pt-2 border-t border-rose-100 dark:border-rose-900/40">
              {harianStudentsByStatus.alpa.length > 0 ? (
                <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                  <div className="text-[10px] font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider mb-0.5">Daftar Siswa Alpa:</div>
                  {harianStudentsByStatus.alpa.map((st) => (
                    <div key={st.id} className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-start gap-1">
                      <span className="text-rose-500 font-bold shrink-0">•</span>
                      <span className="leading-tight">
                        {st.name}
                        {st.note && <span className="block text-[10px] text-rose-600/80 dark:text-rose-400/80 font-normal italic">({st.note})</span>}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[10px] text-slate-400 dark:text-slate-500 italic">Nihil (0 Siswa)</div>
              )}
            </div>
          )}
        </div>

        {/* Terlambat (T) */}
        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-purple-200 dark:border-purple-900/50 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400">
                Terlambat (T)
              </div>
              <div className="text-xl font-black text-purple-600 dark:text-purple-400 mt-0.5">
                {rekapStats.terlambat}
              </div>
            </div>
            <div className="p-2 bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 rounded-lg shrink-0">
              <Clock size={18} />
            </div>
          </div>
          {viewMode === 'harian' && (
            <div className="mt-2.5 pt-2 border-t border-purple-100 dark:border-purple-900/40">
              {harianStudentsByStatus.terlambat.length > 0 ? (
                <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                  <div className="text-[10px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-0.5">Daftar Terlambat:</div>
                  {harianStudentsByStatus.terlambat.map((st) => (
                    <div key={st.id} className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-start gap-1">
                      <span className="text-purple-500 font-bold shrink-0">•</span>
                      <span className="leading-tight">
                        {st.name}
                        {st.note && <span className="block text-[10px] text-purple-600/80 dark:text-purple-400/80 font-normal italic">({st.note})</span>}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[10px] text-slate-400 dark:text-slate-500 italic">Nihil (0 Siswa)</div>
              )}
            </div>
          )}
        </div>

        {/* Pulang Sebelum Waktunya (P) */}
        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-orange-200 dark:border-orange-900/50 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400">
                Pulang Awal (P)
              </div>
              <div className="text-xl font-black text-orange-600 dark:text-orange-400 mt-0.5">
                {rekapStats.pulang}
              </div>
            </div>
            <div className="p-2 bg-orange-50 dark:bg-orange-950/60 text-orange-600 dark:text-orange-400 rounded-lg shrink-0">
              <DoorOpen size={18} />
            </div>
          </div>
          {viewMode === 'harian' && (
            <div className="mt-2.5 pt-2 border-t border-orange-100 dark:border-orange-900/40">
              {harianStudentsByStatus.pulang.length > 0 ? (
                <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                  <div className="text-[10px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider mb-0.5">Daftar Pulang Awal:</div>
                  {harianStudentsByStatus.pulang.map((st) => (
                    <div key={st.id} className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-start gap-1">
                      <span className="text-orange-500 font-bold shrink-0">•</span>
                      <span className="leading-tight">
                        {st.name}
                        {st.note && <span className="block text-[10px] text-orange-600/80 dark:text-orange-400/80 font-normal italic">({st.note})</span>}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[10px] text-slate-400 dark:text-slate-500 italic">Nihil (0 Siswa)</div>
              )}
            </div>
          )}
        </div>

        {/* Persentase Kehadiran */}
        <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-teal-200 dark:border-teal-900/50 shadow-sm flex items-center justify-between col-span-2 sm:col-span-3 lg:col-span-1">
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500 dark:text-slate-400">
              % Kehadiran
            </div>
            <div className="text-xl font-black text-teal-600 dark:text-teal-400 mt-0.5">
              {rekapStats.percentage}%
            </div>
          </div>
          <div className="p-2 bg-teal-50 dark:bg-teal-950/60 text-teal-600 dark:text-teal-400 rounded-lg shrink-0">
            <TrendingUp size={18} />
          </div>
        </div>
      </div>

      {/* Control Bar: Date / Range Selector & Action Buttons */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        {/* Mode-Specific Date Pickers */}
        <div className="flex flex-wrap items-center gap-3">
          {viewMode === 'harian' && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Pilih Tanggal:</label>
              
              <button
                onClick={handlePrevDay}
                className="p-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl flex items-center justify-center transition-all cursor-pointer"
                title="Hari Sebelumnya"
              >
                <ChevronLeft size={16} />
              </button>

              <div className="relative flex items-center">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  onClick={(e) => {
                    try {
                      e.currentTarget.showPicker();
                    } catch (err) {}
                  }}
                  className="pl-8 pr-3 py-2 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                />
                <Calendar size={14} className="absolute left-2.5 text-slate-400 dark:text-slate-500 pointer-events-none" />
              </div>

              <button
                onClick={handleNextDay}
                className="p-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl flex items-center justify-center transition-all cursor-pointer"
                title="Hari Berikutnya"
              >
                <ChevronRight size={16} />
              </button>

              <button
                onClick={() => setSelectedDate(todayStr)}
                className="px-2.5 py-2 text-xs font-bold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl"
              >
                Hari Ini
              </button>
            </div>
          )}

          {viewMode === 'mingguan' && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Pilih Tanggal Minggu:</label>
              <div className="relative flex items-center">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  onClick={(e) => {
                    try {
                      e.currentTarget.showPicker();
                    } catch (err) {}
                  }}
                  className="pl-8 pr-3 py-2 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                />
                <Calendar size={14} className="absolute left-2.5 text-slate-400 dark:text-slate-500 pointer-events-none" />
              </div>
              <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2.5 py-1.5 rounded-xl border border-emerald-200 dark:border-emerald-800">
                {currentWeekDates[0].formattedDate} s/d {currentWeekDates[5].formattedDate}
              </span>
            </div>
          )}

          {viewMode === 'bulanan' && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Pilih Bulan & Tahun:</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {INDONESIAN_MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.name}
                  </option>
                ))}
              </select>

              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {[2025, 2026, 2027].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          )}

          {viewMode === 'semester' && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800/80 rounded-xl text-xs">
                <span className="font-bold text-emerald-800 dark:text-emerald-300">Awal:</span>
                <span className="font-extrabold text-slate-800 dark:text-white">{safeFormatDate(semesterStartDate, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                <span className="text-slate-400 mx-0.5">s/d</span>
                <span className="font-bold text-emerald-800 dark:text-emerald-300">Akhir:</span>
                <span className="font-extrabold text-slate-800 dark:text-white">{safeFormatDate(semesterEndDate, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              </div>
              <button
                onClick={() => setIsSettingSemesterOpen(true)}
                className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                title="Ubah Tanggal Awal Masuk & Akhir Semester"
              >
                <Sliders size={13} />
                <span>Ubah Periode</span>
              </button>
            </div>
          )}

          {viewMode === 'tahunan' && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Pilih Tahun:</label>
              <select
                value={selectedYear}
                onChange={(e: any) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 text-xs font-bold bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {[2025, 2026, 2027].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Search & Actions */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-60">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari nama / NIS..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {viewMode === 'harian' && (
            <button
              onClick={handleMarkAllHadir}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-xl shadow transition-all shrink-0 cursor-pointer"
              title="Tandai semua siswa kelas ini sebagai Hadir pada tanggal terpilih"
            >
              <CheckSquare size={16} />
              <span>Semua Hadir</span>
            </button>
          )}

          <button
            onClick={() => setIsPeriodSelectModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 active:scale-98 text-white text-xs font-bold rounded-xl shadow transition-all shrink-0 cursor-pointer"
            title="Analisis Ketidakhadiran Siswa (Sakit, Izin, Alpa)"
          >
            <BarChart2 size={16} />
            <span>Analisis</span>
          </button>

          <button
            onClick={() => handlePrintReport(viewMode === 'semester' || viewMode === 'bulanan' ? 'bulanan_per_halaman' : 'portrait')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow transition-all shrink-0 cursor-pointer"
            title="Cetak Rekapitulasi Kehadiran (A4 Portrait, Bulanan per Halaman)"
          >
            <Printer size={16} />
            <span>Cetak Rekap</span>
          </button>
        </div>
      </div>

      {/* VIEW MODE 1: HARIAN */}
      {viewMode === 'harian' && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/60 pb-3">
            <h4 className="font-bold text-slate-800 dark:text-white text-sm flex items-center gap-2">
              <span>Input & Rekapitulasi Presensi Harian ({classStudents.length} Siswa)</span>
              <span className="text-xs font-semibold text-slate-500">
                • {safeFormatDate(selectedDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </h4>
          </div>

          {/* Banner Notifikasi Agenda KBM Terhubung */}
          {agendasOnSelectedDate.length > 0 && (
            <div className="p-3 bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 rounded-xl text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-lg shrink-0">
                  <BookOpen size={16} />
                </div>
                <div>
                  <span className="font-bold text-blue-900 dark:text-blue-200">
                    Terhubung {agendasOnSelectedDate.length} Agenda Pembelajaran:
                  </span>{' '}
                  <span className="text-blue-700 dark:text-blue-300 font-medium">
                    {agendasOnSelectedDate.map((a) => `${a.subject} (${a.teacherName || 'Guru Mapel'})`).join(', ')}
                  </span>
                  <div className="text-[11px] text-blue-600 dark:text-blue-400">
                    Status kehadiran otomatis tersinkron dari agenda KBM guru yang mengajar di kelas {homeroomClass}.
                  </div>
                </div>
              </div>
              {onUpdateAttendance && (
                <button
                  onClick={handleSyncFromAgenda}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs shrink-0 self-start sm:self-auto shadow-xs cursor-pointer transition-all"
                  title="Simpan data gabungan dari agenda ke database wali kelas"
                >
                  Sinkronkan Permanen
                </button>
              )}
            </div>
          )}

          {/* Feedback Sinkronisasi */}
          {syncFeedback && (
            <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 rounded-xl text-xs flex items-center gap-2">
              <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />
              <span>{syncFeedback}</span>
            </div>
          )}

          {/* Banner Peringatan Sakit >= 3 Hari Berturut-turut */}
          {consecutiveSickWarnings.length > 0 && (
            <div className="p-3.5 bg-rose-50 dark:bg-rose-950/40 border-2 border-rose-400 dark:border-rose-700/80 rounded-2xl text-xs space-y-2.5 shadow-sm animate-pulse-once">
              <div className="flex items-center justify-between gap-2">
                <div className="font-black text-rose-800 dark:text-rose-200 flex items-center gap-2 text-sm">
                  <AlertOctagon size={18} className="text-rose-600 dark:text-rose-400 shrink-0" />
                  <span>PERINGATAN KESEHATAN: Siswa Sakit ≥3 Hari Berturut-turut</span>
                </div>
                <span className="px-2.5 py-0.5 bg-rose-600 text-white rounded-full text-[11px] font-black shrink-0">
                  {consecutiveSickWarnings.length} Siswa
                </span>
              </div>
              <p className="text-slate-600 dark:text-slate-300 text-xs">
                Siswa berikut tercatat berstatus <strong className="text-rose-700 dark:text-rose-300">Sakit selama 3 hari berturut-turut</strong> dihitung dari hari yang dipilih ({safeFormatDate(selectedDate, { day: 'numeric', month: 'short', year: 'numeric' })}) ke belakang. Disarankan untuk segera menghubungi wali murid atau melakukan konfirmasi surat dokter:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-1">
                {consecutiveSickWarnings.map((w) => (
                  <div
                    key={`warn-consec-${w.student.id}`}
                    className="p-2.5 bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-900/80 rounded-xl space-y-1 shadow-2xs"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900 dark:text-white text-xs">
                        {w.student.name}
                      </span>
                      <span className="px-1.5 py-0.5 bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300 rounded font-black text-[10px]">
                        {w.consecutiveDays} Hari Sakit
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
                      <span>NIS: {w.student.nis || '-'}</span>
                      <span>•</span>
                      <span>{w.student.gender}</span>
                    </div>
                    <div className="text-[10px] text-slate-600 dark:text-slate-300 pt-0.5">
                      <span className="font-semibold text-rose-600 dark:text-rose-400">Rentang: </span>
                      {w.dates
                        .slice(0, 3)
                        .reverse()
                        .map((d) => safeFormatDate(d, { day: 'numeric', month: 'short' }))
                        .join(' → ')}
                    </div>
                    {w.notes.length > 0 && (
                      <div className="text-[10px] italic text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800 pt-1 mt-1">
                        Ket: {w.notes.join(' | ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Banner Rincian Siswa Tidak Hadir / Keterangan Khusus */}
          {(harianStudentsByStatus.sakit.length > 0 ||
            harianStudentsByStatus.izin.length > 0 ||
            harianStudentsByStatus.alpa.length > 0 ||
            harianStudentsByStatus.terlambat.length > 0 ||
            harianStudentsByStatus.pulang.length > 0) && (
            <div className="p-3.5 bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 rounded-xl text-xs space-y-2">
              <div className="font-bold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 shrink-0" />
                <span>Rincian Siswa Tidak Hadir / Keterangan Khusus Hari Ini ({safeFormatDate(selectedDate, { day: 'numeric', month: 'short', year: 'numeric' })}):</span>
              </div>
              <div className="flex flex-wrap gap-2 pt-0.5">
                {harianStudentsByStatus.sakit.map((s) => (
                  <span key={`b-s-${s.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/60 text-blue-900 dark:text-blue-200 rounded-lg font-bold text-xs shadow-2xs">
                    <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                    Sakit: {s.name} {s.note ? `(${s.note})` : ''}
                  </span>
                ))}
                {harianStudentsByStatus.izin.map((s) => (
                  <span key={`b-i-${s.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/60 text-amber-900 dark:text-amber-200 rounded-lg font-bold text-xs shadow-2xs">
                    <span className="w-2 h-2 rounded-full bg-amber-600"></span>
                    Izin: {s.name} {s.note ? `(${s.note})` : ''}
                  </span>
                ))}
                {harianStudentsByStatus.alpa.map((s) => (
                  <span key={`b-a-${s.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-rose-100 dark:bg-rose-900/60 text-rose-900 dark:text-rose-200 rounded-lg font-bold text-xs shadow-2xs">
                    <span className="w-2 h-2 rounded-full bg-rose-600"></span>
                    Alpa: {s.name} {s.note ? `(${s.note})` : ''}
                  </span>
                ))}
                {harianStudentsByStatus.terlambat.map((s) => (
                  <span key={`b-t-${s.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-100 dark:bg-purple-900/60 text-purple-900 dark:text-purple-200 rounded-lg font-bold text-xs shadow-2xs">
                    <span className="w-2 h-2 rounded-full bg-purple-600"></span>
                    Terlambat: {s.name} {s.note ? `(${s.note})` : ''}
                  </span>
                ))}
                {harianStudentsByStatus.pulang.map((s) => (
                  <span key={`b-p-${s.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-100 dark:bg-orange-900/60 text-orange-900 dark:text-orange-200 rounded-lg font-bold text-xs shadow-2xs">
                    <span className="w-2 h-2 rounded-full bg-orange-600"></span>
                    Pulang Awal: {s.name} {s.note ? `(${s.note})` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            {/* MOBILE VIEW CARDS (Visible on sm:hidden) */}
            <div className="block sm:hidden divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
              {filteredStudents.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-500">
                  Tidak ada siswa ditemukan di Kelas {homeroomClass}.
                </div>
              ) : (
                filteredStudents.map((s, idx) => {
                  const record = getRecord(s.id, selectedDate);
                  const activeStatus = record?.status || AttendanceStatus.Hadir;
                  const activeNote = record?.description || '';

                  return (
                    <div key={`mob-att-${s.id}`} className="p-4 bg-white dark:bg-slate-800 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="w-6 h-6 rounded-lg bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 font-black text-xs flex items-center justify-center shrink-0">
                            {idx + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h4 className="font-bold text-sm text-slate-900 dark:text-white whitespace-normal break-words leading-snug">
                                {s.name} {s.nickname ? `(${s.nickname})` : ''}
                              </h4>
                              {consecutiveSickStudentIds.has(s.id) && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.2 bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900 rounded font-black text-[10px]"
                                  title="Peringatan: Siswa ini sakit 3 hari berturut-turut!"
                                >
                                  <AlertOctagon size={11} className="text-rose-600 dark:text-rose-400" />
                                  Sakit ≥3 Hari
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                              NIS: {s.nis || '-'} • <span className={s.gender === 'Laki-laki' ? 'text-blue-600 dark:text-blue-400' : 'text-pink-600 dark:text-pink-400'}>{s.gender}</span>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Status Selection Buttons */}
                      <div className="space-y-1.5 pt-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Status Kehadiran</span>
                        <div className="grid grid-cols-6 gap-1 p-1 bg-slate-50 dark:bg-slate-900/80 rounded-xl border border-slate-200 dark:border-slate-700">
                          {Object.values(AttendanceStatus).map((statusKey) => {
                            const cfg = getManagerStatusCfg(statusKey);
                            if (!cfg) return null;
                            const isSelected = activeStatus === statusKey;

                            return (
                              <button
                                key={statusKey}
                                onClick={() => handleSetStatus(s.id, statusKey as AttendanceStatus)}
                                className={`py-1.5 rounded-lg text-xs transition-all flex items-center justify-center font-bold ${
                                  isSelected ? cfg.btnActive : cfg.btnInactive
                                }`}
                                title={cfg.label}
                              >
                                {cfg.short}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Note Input */}
                      <div className="pt-1">
                        <input
                          type="text"
                          value={activeNote}
                          onChange={(e) => handleSetNote(s.id, e.target.value)}
                          placeholder="Catatan / Keterangan (opsional)..."
                          className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-white outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* DESKTOP VIEW TABLE (Visible on hidden sm:block) */}
            <TopScrollTable className="hidden sm:flex">
              <table className="w-full text-left text-sm text-slate-700 dark:text-slate-200 border-separate border-spacing-0">
                <thead className="bg-slate-100 dark:bg-slate-900/80 text-xs font-bold uppercase text-slate-600 dark:text-slate-400">
                  <tr>
                    <th className="py-3 px-3 text-center w-12 min-w-[48px] max-w-[48px] sticky left-0 z-20 bg-slate-100 dark:bg-slate-900 border-b border-r border-slate-200 dark:border-slate-700">No</th>
                    <th className="py-3 px-4 min-w-[180px] sm:min-w-[220px] sticky left-12 z-20 bg-slate-100 dark:bg-slate-900 border-b border-r-2 border-slate-300 dark:border-slate-700 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)]">Nama Lengkap Siswa</th>
                    <th className="py-3 px-4 text-center w-20 border-b border-slate-200 dark:border-slate-700">NIS</th>
                    <th className="py-3 px-4 text-center w-16 border-b border-slate-200 dark:border-slate-700">L/P</th>
                    <th className="py-3 px-4 text-center border-b border-slate-200 dark:border-slate-700">Status Kehadiran Hari Ini</th>
                    <th className="py-3 px-4 border-b border-slate-200 dark:border-slate-700">Catatan / Keterangan Khusus</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800">
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-500 border-b border-slate-200 dark:border-slate-700">
                        Tidak ada siswa ditemukan di Kelas {homeroomClass}.
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((s, idx) => {
                      const record = getRecord(s.id, selectedDate);
                      const activeStatus = record?.status || AttendanceStatus.Hadir;
                      const activeNote = record?.description || '';

                      return (
                        <tr key={s.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/40 transition-colors group">
                          <td className="py-3 px-3 text-center font-bold text-slate-500 text-xs w-12 min-w-[48px] max-w-[48px] sticky left-0 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/60 border-b border-r border-slate-200 dark:border-slate-700">
                            {idx + 1}
                          </td>

                          <td className="py-3 px-4 min-w-[180px] sm:min-w-[220px] sticky left-12 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/60 border-b border-r-2 border-slate-300 dark:border-slate-700 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)]">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <div className={`font-bold ${s.gender === 'Laki-laki' ? 'text-blue-600 dark:text-blue-400' : 'text-pink-600 dark:text-pink-400'}`}>
                                {s.name}
                              </div>
                              {consecutiveSickStudentIds.has(s.id) && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.2 bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900 rounded font-black text-[10px]"
                                  title="Peringatan: Siswa ini sakit 3 hari berturut-turut!"
                                >
                                  <AlertOctagon size={11} className="text-rose-600 dark:text-rose-400" />
                                  Sakit ≥3 Hari
                                </span>
                              )}
                            </div>
                            {s.nickname && (
                              <div className="text-xs text-slate-400">({s.nickname})</div>
                            )}
                          </td>

                          <td className="py-3 px-4 text-center text-xs font-mono text-slate-500 border-b border-slate-200 dark:border-slate-700">
                            {s.nis || '-'}
                          </td>

                          <td className="py-3 px-4 text-center border-b border-slate-200 dark:border-slate-700">
                            <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${
                              s.gender === 'Laki-laki' ? 'bg-blue-50 text-blue-600' : 'bg-pink-50 text-pink-600'
                            }`}>
                              {s.gender === 'Laki-laki' ? 'L' : 'P'}
                            </span>
                          </td>

                          {/* Status Buttons Matrix: Hadir(H), Sakit(S), Izin(I), Alpa(A), Terlambat(T), Pulang(P) */}
                          <td className="py-3 px-4 text-center border-b border-slate-200 dark:border-slate-700">
                            <div className="inline-flex flex-wrap items-center justify-center gap-1.5 p-1 bg-slate-50 dark:bg-slate-900/80 rounded-xl border border-slate-200 dark:border-slate-700">
                              {Object.values(AttendanceStatus).map((statusKey) => {
                                const cfg = getManagerStatusCfg(statusKey);
                                if (!cfg) return null;
                                const isSelected = activeStatus === statusKey;

                                return (
                                  <button
                                    key={statusKey}
                                    onClick={() => handleSetStatus(s.id, statusKey as AttendanceStatus)}
                                    className={`px-2.5 py-1 rounded-lg text-xs transition-all ${
                                      isSelected ? cfg.btnActive : cfg.btnInactive
                                    }`}
                                    title={`${cfg.label} (${cfg.short})`}
                                  >
                                    {cfg.short}
                                  </button>
                                );
                              })}
                            </div>
                          </td>

                          {/* Note Input */}
                          <td className="py-3 px-4 border-b border-slate-200 dark:border-slate-700">
                            <input
                              type="text"
                              value={activeNote}
                              onChange={(e) => handleSetNote(s.id, e.target.value)}
                              placeholder="Tuliskan alasan / jam keterlambatan (opsional)..."
                              className="w-full px-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-white outline-none focus:ring-1 focus:ring-emerald-500"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </TopScrollTable>
          </div>
        </div>
      )}

      {/* VIEW MODE 2: MINGGUAN */}
      {viewMode === 'mingguan' && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/60 pb-3">
            <h4 className="font-bold text-slate-800 dark:text-white text-sm">
              Matriks & Rekapitulasi Presensi Mingguan (Senin - Sabtu)
            </h4>
            <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-3 py-1 rounded-full border border-emerald-200 dark:border-emerald-800">
              Periode: {currentWeekDates[0].formattedDate} s/d {currentWeekDates[5].formattedDate}
            </div>
          </div>

          <TopScrollTable>
            <table className="w-full text-left text-xs text-slate-700 dark:text-slate-200 border-separate border-spacing-0">
              <thead className="bg-slate-100 dark:bg-slate-900/80 font-bold uppercase text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="py-3 px-3 text-center w-12 min-w-[48px] max-w-[48px] sticky left-0 z-20 bg-slate-100 dark:bg-slate-900 border-b border-r border-slate-200 dark:border-slate-700">No</th>
                  <th className="py-3 px-3 min-w-[180px] sm:min-w-[220px] sticky left-12 z-20 bg-slate-100 dark:bg-slate-900 border-b border-r-2 border-slate-300 dark:border-slate-700 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)]">Nama Lengkap Siswa</th>
                  {currentWeekDates.map((w) => (
                    <th key={w.dateStr} className="py-3 px-2 text-center min-w-[65px] border-b border-slate-200 dark:border-slate-700">
                      {w.dayName}<br />
                      <span className="text-[10px] text-slate-400 font-normal">{w.formattedDate}</span>
                    </th>
                  ))}
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">H</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">S</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">I</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">A</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">T</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">P</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800">
                {filteredStudents.map((s, idx) => {
                  const weekRecs = currentWeekDates.map((w) => getRecord(s.id, w.dateStr));
                  const weekRecsExist = weekRecs.filter((r): r is AttendanceRecord => !!r);

                  const H_raw = weekRecsExist.filter((r) => r.status === AttendanceStatus.Hadir).length;
                  const S = weekRecsExist.filter((r) => r.status === AttendanceStatus.Sakit).length;
                  const I = weekRecsExist.filter((r) => r.status === AttendanceStatus.Izin).length;
                  const A = weekRecsExist.filter((r) => r.status === AttendanceStatus.Alpa).length;
                  const T = weekRecsExist.filter((r) => r.status === AttendanceStatus.Terlambat).length;
                  const P = weekRecsExist.filter((r) => r.status === AttendanceStatus.Pulang).length;
                  // "T" dan "P" tetap di hadirkan "H"
                  const H = H_raw + T + P;

                  return (
                    <tr key={s.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/40 transition-colors group">
                      <td className="py-2.5 px-3 text-center font-bold text-slate-500 w-12 min-w-[48px] max-w-[48px] sticky left-0 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/60 border-b border-r border-slate-200 dark:border-slate-700">{idx + 1}</td>
                      <td className="py-2.5 px-3 min-w-[180px] sm:min-w-[220px] sticky left-12 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/60 border-b border-r-2 border-slate-300 dark:border-slate-700 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)]">
                        <div className={`font-bold ${s.gender === 'Laki-laki' ? 'text-blue-600 dark:text-blue-400' : 'text-pink-600 dark:text-pink-400'}`}>{s.name}</div>
                        {s.nickname && <div className="text-[10px] text-slate-400">({s.nickname})</div>}
                      </td>

                      {/* Mon to Sat Cells */}
                      {currentWeekDates.map((w, wIdx) => {
                        const rec = weekRecs[wIdx];
                        const cfg = rec ? getManagerStatusCfg(rec.status) : null;

                        return (
                          <td key={w.dateStr} className="py-2.5 px-1.5 text-center border-b border-slate-200 dark:border-slate-700">
                            {cfg ? (
                              <span
                                className={`inline-block w-7 h-7 rounded-lg text-xs font-black leading-7 text-center ${cfg.bg} text-white shadow-xs`}
                                title={`${w.dayName}: ${cfg.label} ${rec?.description ? `(${rec.description})` : ''}`}
                              >
                                {cfg.short}
                              </span>
                            ) : (
                              <span className="inline-block w-7 h-7 rounded-lg text-xs font-bold leading-7 text-center bg-slate-100 dark:bg-slate-700 text-slate-300">
                                -
                              </span>
                            )}
                          </td>
                        );
                      })}

                      <td className="py-2.5 px-2 text-center font-bold text-emerald-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{H}</td>
                      <td className="py-2.5 px-2 text-center font-bold text-blue-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{S}</td>
                      <td className="py-2.5 px-2 text-center font-bold text-amber-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{I}</td>
                      <td className="py-2.5 px-2 text-center font-bold text-rose-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{A}</td>
                      <td className="py-2.5 px-2 text-center font-bold text-purple-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{T}</td>
                      <td className="py-2.5 px-2 text-center font-bold text-orange-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{P}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TopScrollTable>
        </div>
      )}

      {/* VIEW MODE 3: BULANAN */}
      {viewMode === 'bulanan' && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 dark:border-slate-700/60 pb-3 gap-3">
            <h4 className="font-bold text-slate-800 dark:text-white text-sm">
              Rekapitulasi Presensi Bulanan & Presentase Kehadiran
            </h4>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevMonth}
                className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                title="Bulan Sebelumnya"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs font-extrabold text-slate-700 dark:text-slate-200 px-2 min-w-[120px] text-center bg-slate-50 dark:bg-slate-900 py-1.5 rounded-lg border border-slate-200/60 dark:border-slate-700/60">
                {INDONESIAN_MONTHS.find((m) => m.value === selectedMonth)?.name} {selectedYear}
              </span>
              <button
                onClick={handleNextMonth}
                className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all cursor-pointer flex items-center gap-1"
                title="Bulan Selanjutnya"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Panel Analisis Prosentase Bulanan (Formula Khusus) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4 bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-100 dark:border-slate-700/50">
            {/* Card 1: Prosentase Hadir */}
            <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-emerald-200 dark:border-emerald-900/40 shadow-xs">
              <span className="text-[10px] font-black tracking-wider uppercase text-emerald-600 dark:text-emerald-400">PROSENTASE HADIR (H)</span>
              <div className="text-xl font-black text-emerald-700 dark:text-emerald-300 mt-1">
                {monthlyStatsBreakdown.pctHadir.toFixed(2)}%
              </div>
              <div className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 mt-0.5">
                Formula: {monthlyStatsBreakdown.totalHadir} / ({monthlyStatsBreakdown.jmlMurid} × {monthlyStatsBreakdown.totalHariBelajar})
              </div>
            </div>

            {/* Card 2: Prosentase Sakit */}
            <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-blue-200 dark:border-blue-900/40 shadow-xs">
              <span className="text-[10px] font-black tracking-wider uppercase text-blue-600 dark:text-blue-400">PROSENTASE SAKIT (S)</span>
              <div className="text-xl font-black text-blue-700 dark:text-blue-300 mt-1">
                {monthlyStatsBreakdown.pctSakit.toFixed(2)}%
              </div>
              <div className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 mt-0.5">
                Formula: {monthlyStatsBreakdown.totalSakit} / ({monthlyStatsBreakdown.jmlMurid} × {monthlyStatsBreakdown.totalHariBelajar})
              </div>
            </div>

            {/* Card 3: Prosentase Izin */}
            <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-amber-200 dark:border-amber-900/40 shadow-xs">
              <span className="text-[10px] font-black tracking-wider uppercase text-amber-600 dark:text-amber-400">PROSENTASE IZIN (I)</span>
              <div className="text-xl font-black text-amber-700 dark:text-amber-300 mt-1">
                {monthlyStatsBreakdown.pctIzin.toFixed(2)}%
              </div>
              <div className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 mt-0.5">
                Formula: {monthlyStatsBreakdown.totalIzin} / ({monthlyStatsBreakdown.jmlMurid} × {monthlyStatsBreakdown.totalHariBelajar})
              </div>
            </div>

            {/* Card 4: Prosentase Alpa */}
            <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-rose-200 dark:border-rose-900/40 shadow-xs">
              <span className="text-[10px] font-black tracking-wider uppercase text-rose-600 dark:text-rose-400">PROSENTASE ALPA (A)</span>
              <div className="text-xl font-black text-rose-700 dark:text-rose-300 mt-1">
                {monthlyStatsBreakdown.pctAlpa.toFixed(2)}%
              </div>
              <div className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 mt-0.5">
                Formula: {monthlyStatsBreakdown.totalAlpa} / ({monthlyStatsBreakdown.jmlMurid} × {monthlyStatsBreakdown.totalHariBelajar})
              </div>
            </div>

            {/* Card 5: Prosentase Absensi */}
            <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-purple-200 dark:border-purple-900/40 shadow-xs">
              <span className="text-[10px] font-black tracking-wider uppercase text-purple-600 dark:text-purple-400">PROSENTASE ABSENSI</span>
              <div className="text-xl font-black text-purple-700 dark:text-purple-300 mt-1">
                {monthlyStatsBreakdown.pctAbsensi.toFixed(2)}%
              </div>
              <div className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 mt-0.5">
                Formula: ({monthlyStatsBreakdown.totalSakit} + {monthlyStatsBreakdown.totalIzin} + {monthlyStatsBreakdown.totalAlpa}) / ({monthlyStatsBreakdown.jmlMurid} × {monthlyStatsBreakdown.totalHariBelajar})
              </div>
            </div>

            {/* Card 6: Prosentase Rata-rata */}
            <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-indigo-200 dark:border-indigo-900/40 shadow-xs">
              <span className="text-[10px] font-black tracking-wider uppercase text-indigo-600 dark:text-indigo-400">PROSENTASE RATA2</span>
              <div className="text-xl font-black text-indigo-700 dark:text-indigo-300 mt-1">
                {monthlyStatsBreakdown.pctRata2.toFixed(2)}%
              </div>
              <div className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 mt-0.5">
                Formula: ({monthlyStatsBreakdown.pctSakit.toFixed(1)}% + {monthlyStatsBreakdown.pctIzin.toFixed(1)}% + {monthlyStatsBreakdown.pctAlpa.toFixed(1)}%) / 3
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 bg-slate-100/50 dark:bg-slate-900/30 px-4 py-2 rounded-xl gap-2">
            <div>
              <strong>Keterangan Bulan Ini:</strong> Jumlah Hari Belajar Efektif (Senin - Jumat) = <span className="font-extrabold text-indigo-600 dark:text-indigo-400">{monthlyStatsBreakdown.totalHariBelajar} hari</span>
            </div>
            <div>
              <strong>Jumlah Siswa Terdaftar:</strong> <span className="font-extrabold text-indigo-600 dark:text-indigo-400">{monthlyStatsBreakdown.jmlMurid} siswa</span>
            </div>
          </div>

          {/* Table of Monthly Attendance */}
          <TopScrollTable>
            <table className="w-full text-left text-xs text-slate-700 dark:text-slate-200 border-separate border-spacing-0">
              <thead className="bg-slate-100 dark:bg-slate-900/80 font-bold uppercase text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="py-3 px-3 text-center w-12 min-w-[48px] max-w-[48px] sticky left-0 z-20 bg-slate-100 dark:bg-slate-900 border-b border-r border-slate-200 dark:border-slate-700">No</th>
                  <th className="py-3 px-3 min-w-[180px] sm:min-w-[220px] sticky left-12 z-20 bg-slate-100 dark:bg-slate-900 border-b border-r-2 border-slate-300 dark:border-slate-700 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)]">Nama Lengkap Siswa</th>
                  <th className="py-3 px-3 text-center w-20 min-w-[80px] border-b border-slate-200 dark:border-slate-700">NIS / NISN</th>
                  {monthlyDateList.map((d) => {
                    const monthColor = monthColorMap.get(d.monthKey) || 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200';
                    return (
                      <th key={d.dateStr} className={`py-3 px-2 text-center min-w-[70px] border-b border-l border-r border-slate-200/60 dark:border-slate-700/60 ${monthColor}`}>
                        <span className="text-[10px] opacity-75 font-normal">{d.dayName}</span><br />
                        <span className="text-xs font-bold">{d.formatted}</span>
                      </th>
                    );
                  })}
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-emerald-600 border-b border-slate-200 dark:border-slate-700">H</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-blue-600 border-b border-slate-200 dark:border-slate-700">S</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-amber-600 border-b border-slate-200 dark:border-slate-700">I</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-rose-600 border-b border-slate-200 dark:border-slate-700">A</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-purple-600 border-b border-slate-200 dark:border-slate-700">T</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-orange-600 border-b border-slate-200 dark:border-slate-700">P</th>
                  <th className="py-3 px-3 text-center font-bold bg-slate-200/60 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">% Hadir</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800">
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan={13 + monthlyDateList.length} className="py-8 text-center text-slate-500 border-b border-slate-200 dark:border-slate-700">
                      Tidak ada siswa ditemukan di Kelas {homeroomClass}.
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((s, idx) => {
                    const yearNum = parseInt(selectedYear.toString(), 10);
                    const monthNum = parseInt(selectedMonth, 10);
                    const monthStr = String(monthNum).padStart(2, '0');
                    const monthStart = `${yearNum}-${monthStr}-01`;
                    const lastDayNum = new Date(yearNum, monthNum, 0).getDate();
                    const monthEnd = `${yearNum}-${monthStr}-${String(lastDayNum).padStart(2, '0')}`;

                    const effectiveStart = semesterStartDate > monthStart ? semesterStartDate : monthStart;
                    const semesterEndLimit = semesterEndDate || monthEnd;
                    const todayStrLocal = new Date().toISOString().split('T')[0];
                    const effectiveEndBeforeToday = todayStrLocal < semesterEndLimit ? todayStrLocal : semesterEndLimit;
                    const effectiveEnd = effectiveEndBeforeToday < monthEnd ? effectiveEndBeforeToday : monthEnd;

                    const monthlyRecs = effectiveAttendanceRecords.filter(
                      (r) => r.studentId === s.id && r.date >= effectiveStart && r.date <= effectiveEnd
                    );
                    const bd = getStudentBreakdown(s.id, monthlyRecs);

                    return (
                      <tr key={`month-${s.id}`} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/40 transition-colors group">
                        <td className="py-3 px-3 text-center font-bold text-slate-500 w-12 min-w-[48px] max-w-[48px] sticky left-0 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/60 border-b border-r border-slate-200 dark:border-slate-700">{idx + 1}</td>
                        <td className="py-3 px-3 min-w-[180px] sm:min-w-[220px] sticky left-12 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/60 border-b border-r-2 border-slate-300 dark:border-slate-700 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)]">
                          <div className={`font-bold ${s.gender === 'Laki-laki' ? 'text-blue-600 dark:text-blue-400' : 'text-pink-600 dark:text-pink-400'}`}>{s.name}</div>
                          {s.nickname && <div className="text-[10px] text-slate-400">({s.nickname})</div>}
                        </td>
                        <td className="py-3 px-3 text-center font-mono text-slate-500 min-w-[80px] border-b border-slate-200 dark:border-slate-700">
                          {s.nis || '-'}<br />
                          <small className="text-[10px] text-slate-400">{s.nisn || ''}</small>
                        </td>

                        {monthlyDateList.map((d) => {
                          const rec = getRecord(s.id, d.dateStr);
                          const hasSpecialStatus = rec && rec.status !== AttendanceStatus.Hadir;
                          const effectiveStatus = hasSpecialStatus ? rec.status : AttendanceStatus.Hadir;
                          const cfg = getManagerStatusCfg(effectiveStatus);
                          
                          const solidBg = getMonthSolidBg(d.monthKey);

                          return (
                            <td key={d.dateStr} className={`py-2 px-1 text-center border-b border-l border-r border-slate-200/40 dark:border-slate-700/40 ${solidBg}`}>
                              <select
                                value={effectiveStatus}
                                onChange={(e) => handleSetStatus(s.id, e.target.value as AttendanceStatus, d.dateStr)}
                                className={`w-7 h-7 rounded-md text-[11px] font-black text-center text-white cursor-pointer transition-all hover:scale-110 shadow-xs appearance-none focus:outline-hidden ${cfg.bg}`}
                                style={{ textAlignLast: 'center', padding: 0 }}
                                title={`${d.formatted}: Klik untuk mengubah status (${cfg.label}) ${rec?.description ? `[Catatan: ${rec.description}]` : ''}`}
                              >
                                <option value={AttendanceStatus.Hadir} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">H</option>
                                <option value={AttendanceStatus.Sakit} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">S</option>
                                <option value={AttendanceStatus.Izin} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">I</option>
                                <option value={AttendanceStatus.Alpa} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">A</option>
                                <option value={AttendanceStatus.Terlambat} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">T</option>
                                <option value={AttendanceStatus.Pulang} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">P</option>
                              </select>
                            </td>
                          );
                        })}

                        <td className="py-3 px-2 text-center font-bold text-emerald-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.H}</td>
                        <td className="py-3 px-2 text-center font-bold text-blue-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.S}</td>
                        <td className="py-3 px-2 text-center font-bold text-amber-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.I}</td>
                        <td className="py-3 px-2 text-center font-bold text-rose-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.A}</td>
                        <td className="py-3 px-2 text-center font-bold text-purple-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.T}</td>
                        <td className="py-3 px-2 text-center font-bold text-orange-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.P}</td>
                        <td className="py-3 px-3 text-center font-black bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                          <span
                            className={`px-2 py-0.5 rounded ${
                              bd.Pct >= 90
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                : bd.Pct >= 75
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                                : 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                            }`}
                          >
                            {bd.Pct}%
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </TopScrollTable>
        </div>
      )}

      {/* VIEW MODE 4: SEMESTER (Awal Masuk Sekolah s/d Akhir Semester) */}
      {viewMode === 'semester' && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-700/60 pb-3">
            <div>
              <h4 className="font-bold text-slate-800 dark:text-white text-sm flex items-center gap-2">
                <span>Rekapitulasi Kehadiran Peserta Didik Semester {schoolSettings?.semester || 'Ganjil'}</span>
                <span className="text-xs font-normal text-slate-500">
                  (T.A. {schoolSettings?.academicYear || '2025/2026'})
                </span>
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Akumulasi kehadiran dari awal masuk sekolah ({safeFormatDate(semesterStartDate, { day: 'numeric', month: 'short', year: 'numeric' })}) sampai akhir semester ({safeFormatDate(semesterEndDate, { day: 'numeric', month: 'short', year: 'numeric' })}).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsSettingSemesterOpen(true)}
                className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Sliders size={15} />
                <span>Atur Tanggal Semester</span>
              </button>
              <button
                onClick={() => handlePrintReport('bulanan_per_halaman')}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow flex items-center gap-1.5 cursor-pointer"
                title="Cetak Rekapitulasi Kehadiran (A4 Portrait, Bulanan per Halaman)"
              >
                <Printer size={15} />
                <span>Cetak Rekap Semester</span>
              </button>
            </div>
          </div>

          {/* Semester Analytical KPI Highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-700/60">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase text-slate-500">Total Hari Belajar</span>
                <span className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                  <CalendarDays size={16} />
                </span>
              </div>
              <div className="text-2xl font-black text-slate-800 dark:text-white mt-1">
                {semesterTotalWorkingDays} <span className="text-xs font-bold text-slate-400">hari (Senin-Jumat)</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Telah berjalan: <strong>{semesterElapsedWorkingDays} hari</strong>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-700/60">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase text-slate-500">Rata-rata Kehadiran</span>
                <span className="p-1.5 rounded-lg bg-teal-100 dark:bg-teal-950 text-teal-700 dark:text-teal-300">
                  <TrendingUp size={16} />
                </span>
              </div>
              <div className="text-2xl font-black text-teal-600 dark:text-teal-400 mt-1">
                {rekapStats.percentage}%
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Akumulasi seluruh kelas semester ini
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-700/60">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase text-slate-500">Total Ketidakhadiran</span>
                <span className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300">
                  <Heart size={16} />
                </span>
              </div>
              <div className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
                {rekapStats.sakit + rekapStats.izin + rekapStats.alpa} <span className="text-xs font-bold text-slate-400">hari</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Sakit: {rekapStats.sakit} • Izin: {rekapStats.izin} • Alpa: {rekapStats.alpa}
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-700/60">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase text-slate-500">Disiplin Waktu</span>
                <span className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300">
                  <Clock size={16} />
                </span>
              </div>
              <div className="text-2xl font-black text-purple-600 dark:text-purple-400 mt-1">
                {rekapStats.terlambat + rekapStats.pulang} <span className="text-xs font-bold text-slate-400">kejadian</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Terlambat: {rekapStats.terlambat} • Pulang Awal: {rekapStats.pulang}
              </div>
            </div>
          </div>

          {/* Detailed Semester Date Range Panel */}
          <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700/80 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <CalendarRange size={16} className="text-emerald-600" />
                <span className="font-bold text-xs uppercase text-slate-700 dark:text-slate-300">
                  Rincian Rentang & Daftar Tanggal Efektif Belajar
                </span>
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 font-black text-[10px] rounded-full">
                  {semesterDateList.length} Hari Aktif
                </span>
              </div>
              <button
                onClick={() => setShowSemesterDateDetails(!showSemesterDateDetails)}
                className="text-xs font-bold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 flex items-center gap-1 cursor-pointer"
              >
                {showSemesterDateDetails ? 'Sembunyikan Daftar Tanggal' : 'Tampilkan Daftar Tanggal'}
                <ChevronDown size={14} className={`transform transition-transform ${showSemesterDateDetails ? 'rotate-180' : ''}`} />
              </button>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Perhitungan akumulasi kehadiran dihitung dari tanggal <strong>{safeFormatDate(semesterStartDate, { day: 'numeric', month: 'long', year: 'numeric' })}</strong> sampai dengan hari ini (Senin s.d. Jumat, di luar hari libur nasional/sekolah).
            </p>

            {showSemesterDateDetails && (
              <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                <div className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2">
                  Daftar Tanggal Efektif Belajar ({semesterDateList.length} Hari):
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-1.5 max-h-48 overflow-y-auto pr-1">
                  {semesterDateList.map((d, i) => (
                    <div key={d.dateStr} className="bg-white dark:bg-slate-800 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-[11px] flex flex-col">
                      <span className="font-bold text-slate-800 dark:text-white">{i + 1}. {d.dayName}</span>
                      <span className="text-slate-500 text-[10px]">{d.formatted}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Table of Semester Attendance */}
          <TopScrollTable>
            <table className="w-full text-left text-xs text-slate-700 dark:text-slate-200 border-separate border-spacing-0">
              <thead className="bg-slate-100 dark:bg-slate-900/80 font-bold uppercase text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="py-3 px-3 text-center w-12 min-w-[48px] max-w-[48px] sticky left-0 z-20 bg-slate-100 dark:bg-slate-900 border-b border-r border-slate-200 dark:border-slate-700">No</th>
                  <th className="py-3 px-3 min-w-[180px] sm:min-w-[220px] sticky left-12 z-20 bg-slate-100 dark:bg-slate-900 border-b border-r-2 border-slate-300 dark:border-slate-700 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)]">Nama Lengkap Siswa</th>
                  <th className="py-3 px-3 text-center w-20 min-w-[80px] border-b border-slate-200 dark:border-slate-700">NIS / NISN</th>
                  {semesterDateList.map((d) => {
                    const monthColor = monthColorMap.get(d.monthKey) || 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200';
                    return (
                      <th key={d.dateStr} className={`py-3 px-2 text-center min-w-[70px] border-b border-l border-r border-slate-200/60 dark:border-slate-700/60 ${monthColor}`}>
                        <span className="text-[10px] opacity-75 font-normal">{d.dayName}</span><br />
                        <span className="text-xs font-bold">{d.formatted}</span>
                      </th>
                    );
                  })}
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-emerald-600 border-b border-slate-200 dark:border-slate-700">H</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-blue-600 border-b border-slate-200 dark:border-slate-700">S</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-amber-600 border-b border-slate-200 dark:border-slate-700">I</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-rose-600 border-b border-slate-200 dark:border-slate-700">A</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-purple-600 border-b border-slate-200 dark:border-slate-700">T</th>
                  <th className="py-3 px-2 text-center bg-slate-200/60 dark:bg-slate-800 text-orange-600 border-b border-slate-200 dark:border-slate-700">P</th>
                  <th className="py-3 px-3 text-center font-bold bg-slate-200/60 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">% Hadir</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800">
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan={13 + semesterDateList.length} className="py-8 text-center text-slate-500 border-b border-slate-200 dark:border-slate-700">
                      Tidak ada siswa ditemukan di Kelas {homeroomClass}.
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((s, idx) => {
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);
                    const todayStrLocal = today.toISOString().split('T')[0];
                    const effectiveEnd = semesterEndDate < todayStrLocal ? semesterEndDate : todayStrLocal;

                    const semRecs = effectiveAttendanceRecords.filter(
                      (r) => r.studentId === s.id && r.date >= semesterStartDate && r.date <= effectiveEnd
                    );
                    const bd = getStudentBreakdown(s.id, semRecs);

                    return (
                      <tr key={`sem-${s.id}`} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/40 transition-colors group">
                        <td className="py-3 px-3 text-center font-bold text-slate-500 w-12 min-w-[48px] max-w-[48px] sticky left-0 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/60 border-b border-r border-slate-200 dark:border-slate-700">{idx + 1}</td>
                        <td className="py-3 px-3 min-w-[180px] sm:min-w-[220px] sticky left-12 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/60 border-b border-r-2 border-slate-300 dark:border-slate-700 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)]">
                          <div className={`font-bold ${s.gender === 'Laki-laki' ? 'text-blue-600 dark:text-blue-400' : 'text-pink-600 dark:text-pink-400'}`}>{s.name}</div>
                          {s.nickname && <div className="text-[10px] text-slate-400">({s.nickname})</div>}
                        </td>
                        <td className="py-3 px-3 text-center font-mono text-slate-500 min-w-[80px] border-b border-slate-200 dark:border-slate-700">
                          {s.nis || '-'}<br />
                          <small className="text-[10px] text-slate-400">{s.nisn || ''}</small>
                        </td>

                        {semesterDateList.map((d) => {
                          const rec = getRecord(s.id, d.dateStr);
                          const hasSpecialStatus = rec && rec.status !== AttendanceStatus.Hadir;
                          const effectiveStatus = hasSpecialStatus ? rec.status : AttendanceStatus.Hadir;
                          const cfg = getManagerStatusCfg(effectiveStatus);
                          
                          const solidBg = getMonthSolidBg(d.monthKey);

                          return (
                            <td key={d.dateStr} className={`py-2 px-1 text-center border-b border-l border-r border-slate-200/40 dark:border-slate-700/40 ${solidBg}`}>
                              <select
                                value={effectiveStatus}
                                onChange={(e) => handleSetStatus(s.id, e.target.value as AttendanceStatus, d.dateStr)}
                                className={`w-7 h-7 rounded-md text-[11px] font-black text-center text-white cursor-pointer transition-all hover:scale-110 shadow-xs appearance-none focus:outline-hidden ${cfg.bg}`}
                                style={{ textAlignLast: 'center', padding: 0 }}
                                title={`${d.formatted}: Klik untuk mengubah status (${cfg.label}) ${rec?.description ? `[Catatan: ${rec.description}]` : ''}`}
                              >
                                <option value={AttendanceStatus.Hadir} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">H</option>
                                <option value={AttendanceStatus.Sakit} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">S</option>
                                <option value={AttendanceStatus.Izin} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">I</option>
                                <option value={AttendanceStatus.Alpa} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">A</option>
                                <option value={AttendanceStatus.Terlambat} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">T</option>
                                <option value={AttendanceStatus.Pulang} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold">P</option>
                              </select>
                            </td>
                          );
                        })}

                        <td className="py-3 px-2 text-center font-bold text-emerald-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.H}</td>
                        <td className="py-3 px-2 text-center font-bold text-blue-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.S}</td>
                        <td className="py-3 px-2 text-center font-bold text-amber-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.I}</td>
                        <td className="py-3 px-2 text-center font-bold text-rose-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.A}</td>
                        <td className="py-3 px-2 text-center font-bold text-purple-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.T}</td>
                        <td className="py-3 px-2 text-center font-bold text-orange-600 bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">{bd.P}</td>
                        <td className="py-3 px-3 text-center font-black bg-slate-50/80 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                          <span
                            className={`px-2 py-0.5 rounded ${
                              bd.Pct >= 90
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                : bd.Pct >= 75
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                                : 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                            }`}
                          >
                            {bd.Pct}%
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </TopScrollTable>
        </div>
      )}

      {/* VIEW MODE 5: TAHUNAN */}
      {viewMode === 'tahunan' && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/80 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700/60 pb-3">
            <div>
              <h4 className="font-bold text-slate-800 dark:text-white text-sm">
                Rekapitulasi Kehadiran Tahunan ({selectedYear}) Untuk Evaluasi
              </h4>
              <p className="text-xs text-slate-500">
                Akumulasi seluruh presensi harian siswa selama 1 tahun untuk keperluan evaluasi & kenaikan kelas Wali Kelas.
              </p>
            </div>
            <button
              onClick={() => handlePrintReport('landscape')}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow flex items-center gap-1.5"
            >
              <Printer size={16} /> Cetak Rekap Tahunan
            </button>
          </div>

          <TopScrollTable>
            <table className="w-full text-left text-xs text-slate-700 dark:text-slate-200 border-separate border-spacing-0">
              <thead className="bg-slate-100 dark:bg-slate-900/80 font-bold uppercase text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="py-3 px-3 text-center w-12 min-w-[48px] max-w-[48px] sticky left-0 z-20 bg-slate-100 dark:bg-slate-900 border-b border-r border-slate-200 dark:border-slate-700">No</th>
                  <th className="py-3 px-3 min-w-[180px] sm:min-w-[220px] sticky left-12 z-20 bg-slate-100 dark:bg-slate-900 border-b border-r-2 border-slate-300 dark:border-slate-700 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)]">Nama Lengkap Siswa</th>
                  <th className="py-3 px-3 text-center w-20 min-w-[80px] border-b border-slate-200 dark:border-slate-700">NIS / NISN</th>
                  <th className="py-3 px-3 text-center text-emerald-600 border-b border-slate-200 dark:border-slate-700">Hadir</th>
                  <th className="py-3 px-3 text-center text-blue-600 border-b border-slate-200 dark:border-slate-700">Sakit</th>
                  <th className="py-3 px-3 text-center text-amber-600 border-b border-slate-200 dark:border-slate-700">Izin</th>
                  <th className="py-3 px-3 text-center text-rose-600 border-b border-slate-200 dark:border-slate-700">Alpa</th>
                  <th className="py-3 px-3 text-center text-purple-600 border-b border-slate-200 dark:border-slate-700">Terlambat</th>
                  <th className="py-3 px-3 text-center text-orange-600 border-b border-slate-200 dark:border-slate-700">Pulang Awal</th>
                  <th className="py-3 px-3 text-center border-b border-slate-200 dark:border-slate-700">% Kehadiran</th>
                  <th className="py-3 px-3 border-b border-slate-200 dark:border-slate-700">Catatan Perwalian Tahunan</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800">
                {filteredStudents.map((s, idx) => {
                  const yrRecs = effectiveAttendanceRecords.filter(
                    (r) => r.studentId === s.id && r.date.startsWith(selectedYear.toString())
                  );
                  const bd = getStudentBreakdown(s.id, yrRecs);

                  return (
                    <tr key={s.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-700/40 transition-colors group">
                      <td className="py-3 px-3 text-center font-bold text-slate-500 w-12 min-w-[48px] max-w-[48px] sticky left-0 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/60 border-b border-r border-slate-200 dark:border-slate-700">{idx + 1}</td>
                      <td className="py-3 px-3 min-w-[180px] sm:min-w-[220px] sticky left-12 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-700/60 border-b border-r-2 border-slate-300 dark:border-slate-700 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.08)] font-bold text-slate-900 dark:text-white">{s.name}</td>
                      <td className="py-3 px-3 text-center font-mono text-slate-500 min-w-[80px] border-b border-slate-200 dark:border-slate-700">
                        {s.nis || '-'}<br /><small className="text-[10px] text-slate-400">{s.nisn || ''}</small>
                      </td>

                      <td className="py-3 px-3 text-center font-bold text-emerald-600 border-b border-slate-200 dark:border-slate-700">{bd.H}</td>
                      <td className="py-3 px-3 text-center font-bold text-blue-600 border-b border-slate-200 dark:border-slate-700">{bd.S}</td>
                      <td className="py-3 px-3 text-center font-bold text-amber-600 border-b border-slate-200 dark:border-slate-700">{bd.I}</td>
                      <td className="py-3 px-3 text-center font-bold text-rose-600 border-b border-slate-200 dark:border-slate-700">{bd.A}</td>
                      <td className="py-3 px-3 text-center font-bold text-purple-600 border-b border-slate-200 dark:border-slate-700">{bd.T}</td>
                      <td className="py-3 px-3 text-center font-bold text-orange-600 border-b border-slate-200 dark:border-slate-700">{bd.P}</td>

                      <td className="py-3 px-3 text-center border-b border-slate-200 dark:border-slate-700">
                        <span className={`px-2 py-0.5 rounded font-black ${
                          bd.Pct >= 90 ? 'bg-emerald-100 text-emerald-800' : bd.Pct >= 75 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'
                        }`}>
                          {bd.Pct}%
                        </span>
                      </td>

                      <td className="py-3 px-3 text-slate-600 dark:text-slate-300 italic border-b border-slate-200 dark:border-slate-700">
                        {bd.A === 0 && bd.S === 0 && bd.I === 0 ? 'Sangat baik, mempertahankan tingkat kehadiran 100%.' : `Total ketidakhadiran: ${bd.S + bd.I + bd.A} hari (${bd.S}S, ${bd.I}I, ${bd.A}A).`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TopScrollTable>
        </div>
      )}

      {/* MODAL: SET AWAL MASUK SEKOLAH & AKHIR SEMESTER DENGAN SINKRONISASI CLOUD */}
      {isSettingSemesterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200 overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-2xl w-full shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col max-h-[90vh] my-auto animate-in zoom-in-95 duration-200 overflow-hidden">
            {/* Modal Header - Fixed */}
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/80 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 rounded-2xl shadow-xs">
                  <CalendarRange size={22} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-black text-base text-slate-900 dark:text-white">
                      Set Periode Semester Kelas {homeroomClass}
                    </h3>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-100 text-sky-700 dark:bg-sky-950/80 dark:text-sky-300 border border-sky-200 dark:border-sky-800">
                      <Cloud size={10} />
                      <span>Firebase Cloud</span>
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Atur awal masuk KBM, akhir semester & hari libur khusus kelas ini
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsSettingSemesterOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-all cursor-pointer"
                title="Tutup Modal"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
              {/* Quick Presets */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                  <Sparkles size={14} className="text-amber-500" />
                  <span>Pilih Template Cepat Kalender Pendidikan:</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleApplyPreset('ganjil_current')}
                    className="p-2.5 text-left bg-slate-50 hover:bg-emerald-50/70 dark:bg-slate-900/60 dark:hover:bg-emerald-950/40 border border-slate-200 hover:border-emerald-300 dark:border-slate-700 rounded-xl transition-all text-xs cursor-pointer group"
                  >
                    <div className="font-bold text-slate-800 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-300">
                      Semester Ganjil 2025/2026
                    </div>
                    <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                      14 Jul 2025 – 19 Des 2025
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyPreset('genap_current')}
                    className="p-2.5 text-left bg-slate-50 hover:bg-emerald-50/70 dark:bg-slate-900/60 dark:hover:bg-emerald-950/40 border border-slate-200 hover:border-emerald-300 dark:border-slate-700 rounded-xl transition-all text-xs cursor-pointer group"
                  >
                    <div className="font-bold text-slate-800 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-300">
                      Semester Genap 2025/2026
                    </div>
                    <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                      05 Jan 2026 – 19 Jun 2026
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyPreset('ganjil_next')}
                    className="p-2.5 text-left bg-slate-50 hover:bg-emerald-50/70 dark:bg-slate-900/60 dark:hover:bg-emerald-950/40 border border-slate-200 hover:border-emerald-300 dark:border-slate-700 rounded-xl transition-all text-xs cursor-pointer group"
                  >
                    <div className="font-bold text-slate-800 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-300">
                      Semester Ganjil 2026/2027
                    </div>
                    <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                      13 Jul 2026 – 18 Des 2026
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyPreset('genap_next')}
                    className="p-2.5 text-left bg-slate-50 hover:bg-emerald-50/70 dark:bg-slate-900/60 dark:hover:bg-emerald-950/40 border border-slate-200 hover:border-emerald-300 dark:border-slate-700 rounded-xl transition-all text-xs cursor-pointer group"
                  >
                    <div className="font-bold text-slate-800 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-300">
                      Semester Genap 2026/2027
                    </div>
                    <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                      04 Jan 2027 – 18 Jun 2027
                    </div>
                  </button>
                </div>
              </div>

              {/* Custom Date Pickers */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-50/80 dark:bg-slate-900/40 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-700">
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                    Tanggal Awal Masuk Sekolah (KBM):
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={tempStartDate}
                      onChange={(e) => setTempStartDate(e.target.value)}
                      onClick={(e) => {
                        try {
                          e.currentTarget.showPicker();
                        } catch (err) {}
                      }}
                      className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-xl text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                    />
                    <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 leading-snug">
                    Hari pertama aktif kegiatan belajar mengajar / MPLS semester ini.
                  </p>
                </div>

                <div className="bg-slate-50/80 dark:bg-slate-900/40 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-700">
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                    Tanggal Akhir Semester (Rapor):
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={tempEndDate}
                      onChange={(e) => setTempEndDate(e.target.value)}
                      onClick={(e) => {
                        try {
                          e.currentTarget.showPicker();
                        } catch (err) {}
                      }}
                      className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-xl text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                    />
                    <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 leading-snug">
                    Batas akhir rentang presensi dan tanggal pembagian buku Rapor.
                  </p>
                </div>
              </div>

              {/* Custom Holidays Management Section */}
              <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-800 dark:text-white flex items-center gap-1.5">
                    <Calendar size={15} className="text-rose-500" />
                    <span>Tambah Hari Libur Manual (Selain Sabtu & Minggu):</span>
                  </label>
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
                    {tempHolidays.length} hari libur terdaftar
                  </span>
                </div>

                <div className="p-3.5 bg-slate-50/80 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative w-full sm:w-44">
                      <input
                        type="date"
                        value={newHolidayDate}
                        onChange={(e) => setNewHolidayDate(e.target.value)}
                        onClick={(e) => {
                          try {
                            e.currentTarget.showPicker();
                          } catch (err) {}
                        }}
                        className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Keterangan Libur (Cth: Libur Nasional, Isra Miraj, Cuti Bersama)..."
                      value={newHolidayDesc}
                      onChange={(e) => setNewHolidayDesc(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddHoliday();
                        }
                      }}
                      className="flex-1 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl text-xs text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <button
                      type="button"
                      onClick={handleAddHoliday}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                    >
                      <Plus size={15} />
                      <span>Tambah Libur</span>
                    </button>
                  </div>

                  {tempHolidays.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 divide-y divide-slate-100 dark:divide-slate-800">
                      {tempHolidays.map((h) => (
                        <div
                          key={h.date}
                          className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/80 rounded-xl text-xs hover:border-slate-300 dark:hover:border-slate-600 transition-all shadow-2xs"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="font-extrabold text-rose-600 dark:text-rose-400 shrink-0 bg-rose-50 dark:bg-rose-950/70 px-2 py-0.5 rounded-lg border border-rose-200 dark:border-rose-900">
                              {new Date(h.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            <span className="text-slate-700 dark:text-slate-200 font-medium truncate">
                              {h.description}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveHoliday(h.date)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/50 cursor-pointer shrink-0 ml-2"
                            title="Hapus Hari Libur"
                          >
                            <X size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 bg-white dark:bg-slate-800/60 rounded-xl text-center text-xs text-slate-400 dark:text-slate-500 italic border border-dashed border-slate-200 dark:border-slate-700">
                      Belum ada hari libur manual yang ditambahkan untuk kelas {homeroomClass}.
                    </div>
                  )}
                </div>
              </div>

              {/* Calculated Preview Card */}
              <div className="p-4 bg-emerald-50/90 dark:bg-emerald-950/40 rounded-2xl border border-emerald-200 dark:border-emerald-800/70 flex items-start gap-3">
                <Info size={19} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <div className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                  <span className="font-extrabold text-emerald-800 dark:text-emerald-300">
                    Estimasi Hari Kerja Efektif Belajar:
                  </span>{' '}
                  Berdasarkan rentang semester di atas & <strong>{tempHolidays.length} hari libur manual</strong> (di luar Sabtu & Minggu), terdapat{' '}
                  <strong className="text-emerald-800 dark:text-emerald-200 font-black text-sm bg-emerald-200/60 dark:bg-emerald-900/60 px-1.5 py-0.5 rounded">
                    {(() => {
                      if (!tempStartDate || !tempEndDate) return 0;
                      const start = new Date(tempStartDate);
                      const end = new Date(tempEndDate);
                      if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return 0;
                      let count = 0;
                      const curr = new Date(start);
                      while (curr <= end) {
                        const day = curr.getDay();
                        const dateStr = curr.toISOString().split('T')[0];
                        if (day !== 0 && day !== 6 && !tempHolidaySet.has(dateStr)) count++;
                        curr.setDate(curr.getDate() + 1);
                      }
                      return count;
                    })()} hari kerja efektif
                  </strong>{' '}
                  yang dijadikan dasar kalkulasi persentase kehadiran kelas.
                </div>
              </div>

              {saveSuccessMsg && (
                <div className="p-3 bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-200 rounded-xl text-xs font-bold flex items-center gap-2 animate-in fade-in">
                  <CheckCircle size={16} />
                  <span>{saveSuccessMsg}</span>
                </div>
              )}
            </div>

            {/* Modal Footer - Fixed */}
            <div className="px-5 sm:px-6 py-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/80 flex flex-wrap items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                <Cloud size={14} className="text-sky-500 shrink-0" />
                <span>Tersimpan otomatis ke Firebase & sinkron antar-perangkat.</span>
              </div>
              <div className="flex items-center gap-2.5 ml-auto">
                <button
                  type="button"
                  disabled={isSavingSemesterSettings}
                  onClick={() => setIsSettingSemesterOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-700 rounded-xl transition-all cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="button"
                  disabled={isSavingSemesterSettings}
                  onClick={handleSaveSemesterDates}
                  className="px-5 py-2 text-xs font-extrabold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-98 rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSavingSemesterSettings ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      <span>Menyimpan ke Cloud...</span>
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      <span>Simpan Pengaturan Semester</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* POPUP 1: MODAL PILIHAN PERIODE ANALISIS KETIDAKHADIRAN */}
      {isPeriodSelectModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="px-5 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 bg-slate-50/80 dark:bg-slate-900/90">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-tr from-indigo-600 to-violet-500 text-white rounded-2xl shadow-md">
                  <CalendarRange size={20} />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                    <span>Pilihan Periode Analisis</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-extrabold border border-indigo-200 dark:border-indigo-800">
                      Kelas {homeroomClass}
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Pilih rentang waktu presensi yang ingin dianalisis
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsPeriodSelectModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                title="Tutup Modal"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content: Dua Pilihan (Bulanan vs Seluruh Absensi) */}
            <div className="p-5 sm:p-6 space-y-4">
              <div className="space-y-3">
                {/* Opsi 1: Bulanan */}
                <div
                  onClick={() => setAnalysisScope('bulanan')}
                  className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                    analysisScope === 'bulanan'
                      ? 'border-indigo-600 bg-indigo-50/40 dark:bg-indigo-950/30 shadow-sm'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start gap-3.5">
                    <div
                      className={`p-2.5 rounded-xl transition-colors ${
                        analysisScope === 'bulanan'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      <Calendar size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-black text-slate-900 dark:text-white">
                          Bulanan
                        </h4>
                        <span
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            analysisScope === 'bulanan'
                              ? 'border-indigo-600 bg-indigo-600'
                              : 'border-slate-300 dark:border-slate-600'
                          }`}
                        >
                          {analysisScope === 'bulanan' && (
                            <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                          )}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        Menganalisis siswa dengan catatan Sakit, Izin, atau Alpa pada satu bulan tertentu.
                      </p>

                      {/* Dropdown Bulan & Tahun ketika Bulanan dipilih */}
                      {analysisScope === 'bulanan' && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="mt-3.5 pt-3 border-t border-indigo-100 dark:border-indigo-900/60 flex flex-wrap items-center gap-2"
                        >
                          <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                            Pilih Bulan:
                          </span>
                          <select
                            value={analysisSelectedMonth}
                            onChange={(e) => setAnalysisSelectedMonth(e.target.value)}
                            className="px-3 py-1.5 text-xs font-bold bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            {INDONESIAN_MONTHS.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.name}
                              </option>
                            ))}
                          </select>
                          <select
                            value={analysisSelectedYear}
                            onChange={(e) => setAnalysisSelectedYear(parseInt(e.target.value))}
                            className="px-3 py-1.5 text-xs font-bold bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            {[2025, 2026, 2027].map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Opsi 2: Seluruh Absensi */}
                <div
                  onClick={() => setAnalysisScope('seluruh')}
                  className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                    analysisScope === 'seluruh'
                      ? 'border-indigo-600 bg-indigo-50/40 dark:bg-indigo-950/30 shadow-sm'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 hover:border-slate-300 dark:hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start gap-3.5">
                    <div
                      className={`p-2.5 rounded-xl transition-colors ${
                        analysisScope === 'seluruh'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      <CalendarDays size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-black text-slate-900 dark:text-white">
                          Seluruh Absensi
                        </h4>
                        <span
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            analysisScope === 'seluruh'
                              ? 'border-indigo-600 bg-indigo-600'
                              : 'border-slate-300 dark:border-slate-600'
                          }`}
                        >
                          {analysisScope === 'seluruh' && (
                            <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                          )}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        Menganalisis akumulasi seluruh data presensi kelas yang telah dicatat tanpa batasan bulan.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 sm:px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setIsPeriodSelectModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white hover:bg-slate-200/70 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsPeriodSelectModalOpen(false);
                  setIsAnalysisResultModalOpen(true);
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:scale-98 text-white rounded-xl text-xs font-black shadow-md transition-all cursor-pointer"
              >
                <span>Tampilkan Analisis</span>
                <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* POPUP 2: MODAL RINGKASAN METRIK & RINCIAN KASUS */}
      {isAnalysisResultModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/75 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 w-full max-w-[98vw] 2xl:max-w-7xl rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[95vh] overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="px-5 sm:px-6 py-3.5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 shrink-0 bg-slate-50/90 dark:bg-slate-900/90">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-tr from-indigo-600 to-violet-500 text-white rounded-2xl shadow-md shrink-0">
                  <BarChart2 size={22} />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2 flex-wrap">
                    <span>Ringkasan Metrik & Rincian Data Kehadiran</span>
                    <span className="text-xs px-2.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 font-extrabold border border-indigo-200 dark:border-indigo-800">
                      Kelas {homeroomClass}
                    </span>
                    <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-200/80 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold border border-slate-300 dark:border-slate-700">
                      {analysisScope === 'bulanan'
                        ? `Periode: Bulan ${INDONESIAN_MONTHS.find((m) => m.value === analysisSelectedMonth)?.name} ${analysisSelectedYear}`
                        : 'Periode: Seluruh Data Absensi'}
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Menampilkan data kehadiran seluruh peserta didik beserta rincian ketidakhadiran (S/I/A) untuk monitoring wali kelas.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setIsAnalysisResultModalOpen(false);
                    setIsPeriodSelectModalOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 rounded-xl transition-colors border border-indigo-200 dark:border-indigo-800 cursor-pointer"
                  title="Ganti Periode Analisis"
                >
                  <ArrowLeft size={14} />
                  <span className="hidden sm:inline">Ganti Periode</span>
                </button>

                <button
                  onClick={() => setIsAnalysisResultModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                  title="Tutup Modal"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Controls: Metrics & Search */}
            <div className="px-5 sm:px-6 py-3.5 bg-slate-50/70 dark:bg-slate-800/40 border-b border-slate-100 dark:border-slate-800 space-y-3 shrink-0">
              {/* Metric Summary Bar: Ringkas, Simpel & Nyaman di Dark Mode */}
              <div className="flex flex-wrap items-center gap-2 p-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xs">
                {/* Total Siswa & Hari Efektif */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100/80 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700/70">
                  <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Total Siswa:</span>
                  <span className="text-xs font-black text-slate-800 dark:text-slate-100">
                    {analysisResult.totalClassStudents}
                  </span>
                  <span className="text-[11px] text-slate-400 dark:text-slate-500">|</span>
                  <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Hari Belajar:</span>
                  <span className="text-xs font-black text-slate-800 dark:text-slate-100">
                    {analysisResult.totalWorkingDays} hr
                  </span>
                </div>

                {/* Hadir */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50/80 dark:bg-emerald-950/25 rounded-xl border border-emerald-200/80 dark:border-emerald-900/40 text-emerald-800 dark:text-emerald-300">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                  <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">Hadir:</span>
                  <span className="text-xs font-black">{analysisResult.totalHadir}</span>
                  <span className="text-[10px] font-bold opacity-75">({analysisResult.classAttendanceRate}%)</span>
                </div>

                {/* Sakit */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50/80 dark:bg-blue-950/25 rounded-xl border border-blue-200/80 dark:border-blue-900/40 text-blue-800 dark:text-blue-300">
                  <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0"></span>
                  <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-400">Sakit:</span>
                  <span className="text-xs font-black">{analysisResult.totalSakit}</span>
                </div>

                {/* Izin */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50/80 dark:bg-amber-950/25 rounded-xl border border-amber-200/80 dark:border-amber-900/40 text-amber-800 dark:text-amber-300">
                  <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0"></span>
                  <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-400">Izin:</span>
                  <span className="text-xs font-black">{analysisResult.totalIzin}</span>
                </div>

                {/* Alpa */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50/80 dark:bg-rose-950/25 rounded-xl border border-rose-200/80 dark:border-rose-900/40 text-rose-800 dark:text-rose-300">
                  <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0"></span>
                  <span className="text-[11px] font-semibold text-rose-700 dark:text-rose-400">Alpa:</span>
                  <span className="text-xs font-black">{analysisResult.totalAlpa}</span>
                </div>

                {/* Total SIA */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50/90 dark:bg-red-950/35 rounded-xl border border-red-200/90 dark:border-red-900/50 text-red-800 dark:text-red-300 ml-auto sm:ml-0">
                  <span className="text-[11px] font-black text-red-700 dark:text-red-400 uppercase tracking-tight">Total SIA:</span>
                  <span className="text-xs font-black px-1.5 py-0.2 bg-red-600 dark:bg-red-700 text-white rounded-md shadow-2xs">
                    {analysisResult.totalSIAAccum}
                  </span>
                  <span className="text-[10px] font-medium text-red-600 dark:text-red-400">
                    ({analysisResult.totalAffectedStudents} siswa)
                  </span>
                </div>

                {/* Indikator Peringatan Sakit >= 3 Hari Berturut-turut */}
                {consecutiveSickWarnings.length > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-100 dark:bg-rose-950/70 rounded-xl border border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-200 animate-pulse-once shadow-2xs">
                    <AlertOctagon size={13} className="text-rose-600 dark:text-rose-400 shrink-0" />
                    <span className="text-[11px] font-extrabold">Warning Sakit ≥3 Hari:</span>
                    <span className="text-xs font-black px-1.5 py-0.2 bg-rose-600 text-white rounded-md">
                      {consecutiveSickWarnings.length} siswa
                    </span>
                  </div>
                )}
              </div>

              {/* Search, Filter Tabs & Action Bar */}
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pt-1">
                {/* Segmented Filter Buttons */}
                <div className="inline-flex p-1 bg-slate-200/70 dark:bg-slate-800 rounded-xl gap-1 shrink-0 self-start">
                  <button
                    type="button"
                    onClick={() => setAnalysisFilterOnlySIA(false)}
                    className={`px-3 py-1.5 text-xs font-extrabold rounded-lg transition-all cursor-pointer ${
                      !analysisFilterOnlySIA
                        ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-xs'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    Semua Siswa ({analysisResult.totalClassStudents})
                  </button>
                  <button
                    type="button"
                    onClick={() => setAnalysisFilterOnlySIA(true)}
                    className={`px-3 py-1.5 text-xs font-extrabold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                      analysisFilterOnlySIA
                        ? 'bg-white dark:bg-slate-700 text-rose-700 dark:text-rose-300 shadow-xs'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <span>Siswa dg SIA ({analysisResult.totalAffectedStudents})</span>
                    {analysisResult.totalAffectedStudents > 0 && (
                      <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
                    )}
                  </button>
                </div>

                {/* Search & Sort Controls */}
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5 flex-1 max-w-xl">
                  <div className="relative w-full">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={analysisSearchQuery}
                      onChange={(e) => setAnalysisSearchQuery(e.target.value)}
                      placeholder="Cari nama siswa / NIS..."
                      className="w-full pl-8 pr-3 py-2 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      Urutkan:
                    </span>
                    <select
                      value={analysisSortBy}
                      onChange={(e) =>
                        setAnalysisSortBy(e.target.value as 'name' | 'sia' | 'nis' | 'pct')
                      }
                      className="text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-2.5 py-2 text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 font-semibold cursor-pointer"
                    >
                      <option value="name">Nama Siswa (A-Z)</option>
                      <option value="sia">SIA Terbanyak</option>
                      <option value="pct">% Hadir Terendah</option>
                      <option value="nis">Nomor NIS</option>
                    </select>
                  </div>
                </div>

                {/* Print Button */}
                <button
                  onClick={handlePrintAnalysisReport}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-800 hover:bg-slate-900 active:scale-98 text-white rounded-xl text-xs font-bold transition-all shadow-xs shrink-0 cursor-pointer self-start lg:self-auto"
                  title="Cetak Laporan Hasil Analisis (A4 Portrait)"
                >
                  <Printer size={14} />
                  <span>Cetak Analisis</span>
                </button>
              </div>
            </div>

            {/* Table / List Siswa (Lengkap, Luas & Terlihat Jelas) */}
            <div className="flex-1 overflow-auto p-3 sm:p-5 bg-slate-50/30 dark:bg-slate-900/30">
              {analysisDisplayList.length > 0 ? (
                <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-900 shadow-2xs">
                  <table className="w-full text-left border-collapse min-w-[1100px]">
                    <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-2xs">
                      <tr className="text-[11px] font-black uppercase text-slate-600 dark:text-slate-300">
                        <th className="py-3 px-2.5 text-center w-12">No</th>
                        <th className="py-3 px-3 text-center w-24">NIS</th>
                        <th className="py-3 px-3.5 min-w-[190px]">Nama Peserta Didik</th>
                        <th className="py-3 px-2.5 text-center w-16 text-emerald-700 dark:text-emerald-400 bg-emerald-50/60 dark:bg-emerald-950/20">
                          Hadir
                        </th>
                        <th className="py-3 px-2.5 text-center w-16 text-blue-700 dark:text-blue-400 bg-blue-50/60 dark:bg-blue-950/20">
                          Sakit
                        </th>
                        <th className="py-3 px-2.5 text-center w-16 text-amber-700 dark:text-amber-400 bg-amber-50/60 dark:bg-amber-950/20">
                          Izin
                        </th>
                        <th className="py-3 px-2.5 text-center w-16 text-rose-700 dark:text-rose-400 bg-rose-50/60 dark:bg-rose-950/20">
                          Alpa
                        </th>
                        <th className="py-3 px-2 text-center w-14 text-purple-700 dark:text-purple-400">
                          Telat
                        </th>
                        <th className="py-3 px-2 text-center w-14 text-orange-700 dark:text-orange-400">
                          Pulang
                        </th>
                        <th className="py-3 px-3 text-center w-24 text-red-700 dark:text-red-300 font-black bg-red-100/60 dark:bg-red-950/40">
                          Total SIA
                        </th>
                        <th className="py-3 px-2.5 text-center w-20">% Hadir</th>
                        <th className="py-3 px-4 min-w-[280px]">Rincian Tanggal & Catatan Kasus</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                      {analysisDisplayList.map((item, idx) => {
                        const hasSIA = item.totalSIA > 0;
                        const pct = item.percentageHadir;
                        const pctBadgeClass =
                          pct >= 90
                            ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900'
                            : pct >= 75
                            ? 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900'
                            : 'bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-900';

                        return (
                          <tr
                            key={item.student.id}
                            className={`hover:bg-slate-50/90 dark:hover:bg-slate-800/50 transition-colors ${
                              hasSIA
                                ? 'bg-white dark:bg-slate-900'
                                : 'bg-slate-50/20 dark:bg-slate-900/20'
                            }`}
                          >
                            <td className="py-2.5 px-2.5 text-center font-bold text-slate-500 dark:text-slate-400">
                              {idx + 1}
                            </td>
                            <td className="py-2.5 px-3 text-center font-medium text-slate-600 dark:text-slate-400">
                              {item.student.nis || '-'}
                            </td>
                            <td className="py-2.5 px-3.5 font-bold text-slate-900 dark:text-slate-100">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span>{item.student.name}</span>
                                {item.student.gender && (
                                  <span
                                    className={`text-[9px] px-1 py-0.2 rounded font-extrabold ${
                                      item.student.gender === 'Laki-laki'
                                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400'
                                        : 'bg-pink-50 text-pink-600 dark:bg-pink-950 dark:text-pink-400'
                                    }`}
                                  >
                                    {item.student.gender === 'Laki-laki' ? 'L' : 'P'}
                                  </span>
                                )}
                                {consecutiveSickStudentIds.has(item.student.id) && (
                                  <span
                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.2 bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300 rounded font-black text-[9px] border border-rose-200 dark:border-rose-900"
                                    title="Peringatan: Siswa ini sakit 3 hari berturut-turut!"
                                  >
                                    <AlertOctagon size={10} className="text-rose-600 dark:text-rose-400" />
                                    Sakit ≥3 Hari
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 px-2.5 text-center bg-emerald-50/30 dark:bg-emerald-950/10 font-bold text-emerald-700 dark:text-emerald-300">
                              {item.hadir > 0 ? (
                                <span>{item.hadir}</span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-700">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-2.5 text-center bg-blue-50/30 dark:bg-blue-950/10">
                              {item.sakit > 0 ? (
                                <span className="inline-block px-2 py-0.5 bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 font-extrabold rounded-md border border-blue-200 dark:border-blue-900">
                                  {item.sakit}
                                </span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-700 font-bold">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-2.5 text-center bg-amber-50/30 dark:bg-amber-950/10">
                              {item.izin > 0 ? (
                                <span className="inline-block px-2 py-0.5 bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300 font-extrabold rounded-md border border-amber-200 dark:border-amber-900">
                                  {item.izin}
                                </span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-700 font-bold">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-2.5 text-center bg-rose-50/30 dark:bg-rose-950/10">
                              {item.alpa > 0 ? (
                                <span className="inline-block px-2 py-0.5 bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300 font-extrabold rounded-md border border-rose-200 dark:border-rose-900">
                                  {item.alpa}
                                </span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-700 font-bold">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-2 text-center text-purple-700 dark:text-purple-300 font-bold">
                              {item.terlambat > 0 ? (
                                <span className="px-1.5 py-0.5 bg-purple-50 dark:bg-purple-950/50 rounded-md border border-purple-200 dark:border-purple-800">
                                  {item.terlambat}
                                </span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-700">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-2 text-center text-orange-700 dark:text-orange-300 font-bold">
                              {item.pulang > 0 ? (
                                <span className="px-1.5 py-0.5 bg-orange-50 dark:bg-orange-950/50 rounded-md border border-orange-200 dark:border-orange-800">
                                  {item.pulang}
                                </span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-700">-</span>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-center bg-red-100/40 dark:bg-red-950/20">
                              {item.totalSIA > 0 ? (
                                <span className="inline-block px-2.5 py-0.5 bg-red-600 text-white font-black rounded-lg shadow-2xs">
                                  {item.totalSIA}
                                </span>
                              ) : (
                                <span className="text-emerald-600 dark:text-emerald-400 font-bold text-[11px]">
                                  0 (Nihil)
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 px-2.5 text-center">
                              <span
                                className={`inline-block px-2 py-0.5 text-[11px] font-black rounded-md border ${pctBadgeClass}`}
                              >
                                {pct}%
                              </span>
                            </td>
                            <td className="py-2.5 px-4">
                              {item.details.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                  {item.details.map((d, dIdx) => {
                                    const char =
                                      d.status === AttendanceStatus.Sakit
                                        ? 'Sakit'
                                        : d.status === AttendanceStatus.Izin
                                        ? 'Izin'
                                        : d.status === AttendanceStatus.Alpa
                                        ? 'Alpa'
                                        : d.status === AttendanceStatus.Terlambat
                                        ? 'Telat'
                                        : 'Pulang';
                                    const badgeBg =
                                      d.status === AttendanceStatus.Sakit
                                        ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900'
                                        : d.status === AttendanceStatus.Izin
                                        ? 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900'
                                        : d.status === AttendanceStatus.Alpa
                                        ? 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-900'
                                        : d.status === AttendanceStatus.Terlambat
                                        ? 'bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-900'
                                        : 'bg-orange-50 dark:bg-orange-950/60 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-900';

                                    return (
                                      <span
                                        key={dIdx}
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${badgeBg}`}
                                      >
                                        <strong>{char}</strong> •{' '}
                                        {new Date(d.date).toLocaleDateString('id-ID', {
                                          day: 'numeric',
                                          month: 'short',
                                        })}
                                        {d.description && (
                                          <span className="italic font-normal opacity-90">
                                            ({d.description})
                                          </span>
                                        )}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span className="text-emerald-600 dark:text-emerald-400 font-medium text-xs flex items-center gap-1">
                                  <CheckCircle size={13} />
                                  <span>100% Hadir Lengkap</span>
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 size={24} />
                  </div>
                  <h4 className="text-sm font-bold text-slate-800 dark:text-white">
                    {analysisSearchQuery
                      ? 'Tidak Ditemukan Siswa Sesuai Pencarian'
                      : 'Nihil Ketidakhadiran (Semua Siswa Hadir 100%)'}
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto mt-1">
                    {analysisSearchQuery
                      ? `Pencarian "${analysisSearchQuery}" tidak cocok dengan data siswa pada filter saat ini.`
                      : `Pada pilihan ${analysisScope === 'bulanan' ? 'Bulan Terpilih' : 'Seluruh Absensi'}, tidak ada siswa yang tercatat Sakit, Izin, maupun Alpa.`}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-5 sm:px-6 py-3.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/80 flex items-center justify-between gap-3 shrink-0">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                Menampilkan <strong>{analysisDisplayList.length}</strong> siswa
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsAnalysisResultModalOpen(false);
                    setIsPeriodSelectModalOpen(true);
                  }}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <ArrowLeft size={14} />
                  <span>Ganti Periode</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsAnalysisResultModalOpen(false)}
                  className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold transition-all cursor-pointer"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassAttendanceManager;
